import { useNavigate, useLocation } from "react-router-dom";
import { useState, useRef, useEffect, useMemo } from "react";
import {
    Eye,
    Plus,
    X,
    Search,
    AreaChart,
    Sliders,
    // MapPin,
    // Table,
    Gauge,
    // Target,
    Grid,
    AlignLeft,
    LineChart,
    // Map,
    // FileText,
    Binary,
    // Network,
    PieChart,
    // Locate,
    // Activity,
    // Cloud,
    // Clock,
    // Code,
    BarChart2,
    CandlestickChart,
    // Cpu,
    ArrowLeft,
    Database,
    Info,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Check,
    Play,
    Save,
    Pencil,
    Trash2,
    RefreshCw
} from "lucide-react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart as RePieChart,
    LineChart as ReLineChart, Line,
    Pie,
    Cell,
    AreaChart as ReAreaChart,
    Area,
    ReferenceLine
} from "recharts";
import baseapi from "../../services/baseapi";
import { showError, showSuccess } from "../../utils/toast";
import { useClientLevel } from "../../hooks/useClientLevel";
import DeleteConfirmModal from "./DeleteConfirmModal";
import ColorPickerInput from "./Colorpick";
import { ItemsPerPageDropdown } from "../../components/ItemsPerPageDropdown ";

interface VisType {
    id: string;
    name: string;
    description: string;
    icon: React.ComponentType<any>;
    color: string;
}

interface EntityItem {
    entity: string;
    table_name: string;
    category: string;
    field_count: number;
    has_timestamps: boolean;
    label: string;
}

interface EntityField {
    name: string;
    type: string;
    label: string;
    is_nullable: boolean;
    is_groupable: boolean;
    is_filterable: boolean;
    is_aggregatable: boolean;
    is_jsonb: boolean;
}

interface QueryResponse {
    columns: string[];
    rows: any[][];
}

const VISUALIZATION_TYPES: VisType[] = [
    { id: "area", name: "Area", description: "Display data as a filled area chart to show trends over time.", icon: AreaChart, color: "text-teal-400" },
    // { id: "controls", name: "Controls", description: "Add interactive slider or dropdown inputs to filter other dashboard panels.", icon: Sliders, color: "text-orange-400" },
    // { id: "coordinate_map", name: "Coordinate Map", description: "Display coordinates or geographic points on a map layout.", icon: MapPin, color: "text-teal-400" },
    // { id: "table", name: "Data Table", description: "Display raw or aggregated database records in a tabular list view.", icon: Table, color: "text-teal-400" },
    { id: "gantt", name: "Gantt Chart", description: "Visualize project schedules or task durations along a timeline.", icon: Sliders, color: "text-orange-400" },
    { id: "gauge", name: "Gauge", description: "Show radial status indicators indicating performance or thresholds.", icon: Gauge, color: "text-teal-400" },
    // { id: "goal", name: "Goal", description: "Measure progress towards a specific metric benchmark or target.", icon: Target, color: "text-teal-400" },
    { id: "heatmap", name: "Heat Map", description: "Represent multi-dimensional grid values using color density scales.", icon: Grid, color: "text-teal-400" },
    { id: "horizontal_bar", name: "Horizontal Bar", description: "Render metrics as horizontal comparison bars.", icon: AlignLeft, color: "text-teal-400" },
    { id: "line", name: "Line", description: "Plot continuous sequence lines to analyze trend progressions over time.", icon: LineChart, color: "text-teal-400" },
    // { id: "maps", name: "Maps", description: "Interactive map visualization for geographic region datasets.", icon: Map, color: "text-teal-400" },
    // { id: "markdown", name: "Markdown", description: "Add static or dynamic styled textual documentation context.", icon: FileText, color: "text-teal-400" },
    { id: "metric", name: "Metric", description: "Show prominent count numbers or critical aggregated metric values.", icon: Binary, color: "text-teal-400" },
    // { id: "ppl", name: "PPL", description: "Execute Pipeling Query Language scripts to build dynamic metric series.", icon: Network, color: "text-teal-400" },
    { id: "pie", name: "Pie", description: "Compare proportion ratios or percentage distributions across groups.", icon: PieChart, color: "text-teal-400" },
    // { id: "region_map", name: "Region Map", description: "Map aggregate metrics visually filled across regional boundaries.", icon: Locate, color: "text-teal-400" },
    // { id: "tsvb", name: "TSVB", description: "Time Series Visual Builder for advanced, multi-layer series operations.", icon: Activity, color: "text-teal-400" },
    // { id: "tag_cloud", name: "Tag Cloud", description: "Render tag frequency tags styled proportionally by text size.", icon: Cloud, color: "text-teal-400" },
    // { id: "timeline", name: "Timeline", description: "Track events and logs chronological progressions over time.", icon: Clock, color: "text-teal-400" },
    // { id: "vega", name: "Vega", description: "Create fully custom visualizations using Vega or Vega-Lite schemas.", icon: Code, color: "text-teal-400" },
    { id: "vertical_bar", name: "Vertical Bar", description: "Display traditional vertical bar charts for metric comparisons.", icon: BarChart2, color: "text-teal-400" },
    { id: "candlestick", name: "Candlestick", description: "Trend analysis chart showing open, high, low, close values with bullish/bearish indicators.", icon: CandlestickChart, color: "text-emerald-400" },
    // { id: "vis_builder", name: "VisBuilder", description: "Drag-and-drop workspace builder for rapid visual prototyping.", icon: Cpu, color: "text-teal-400" }
];

// Map visualization type IDs that share config with a base type
const CHART_TYPE_ALIAS: Record<string, string> = {
    vertical_bar: "bar",
    vis_builder: "bar",
    tsvb: "line",
    tag_cloud: "pie",
    region_map: "pie",
    coordinate_map: "pie",
    table: "table",
    heat_map: "heatmap",
};

const DEFAULT_CHART_CONFIGS = [
    { type: "bar", label: "Bar Chart", description: "Display metric values across terms as vertical bars.", x_axis: { required: true, allowed_bucket_types: ["terms", "date_histogram"] }, y_axis: { required: true, allowed_aggregations: ["count", "sum", "avg", "min", "max"] }, split_series: { supported: true, description: "Split bars across terms of a sub-field." } },
    { type: "vertical_bar", label: "Vertical Bar", description: "Display metric values across terms as vertical bars.", x_axis: { required: true, allowed_bucket_types: ["terms", "date_histogram"] }, y_axis: { required: true, allowed_aggregations: ["count", "sum", "avg", "min", "max"] }, split_series: { supported: true, description: "Split bars across terms of a sub-field." } },
    { type: "line", label: "Line Chart", description: "Plot trend data over time buckets.", x_axis: { required: true, allowed_bucket_types: ["date_histogram", "terms"] }, y_axis: { required: true, allowed_aggregations: ["count", "sum", "avg", "min", "max"] }, split_series: { supported: true, description: "Split lines across terms." } },
    { type: "pie", label: "Pie Chart", description: "Compare proportion ratios across categories.", x_axis: { required: true, allowed_bucket_types: ["terms"] }, y_axis: { required: true, allowed_aggregations: ["count", "sum", "avg"] } },
    { type: "donut", label: "Donut Chart", description: "Compare circular slice ratios with a hollow center.", x_axis: { required: true, allowed_bucket_types: ["terms"] }, y_axis: { required: true, allowed_aggregations: ["count", "sum", "avg"] } },
    { type: "area", label: "Area Chart", description: "Display filled area trend series.", x_axis: { required: true, allowed_bucket_types: ["date_histogram"] }, y_axis: { required: true, allowed_aggregations: ["count", "sum", "avg"] }, split_series: { supported: true } },
    { type: "metric", label: "Metric Card", description: "Show a single large summary number.", x_axis: null, y_axis: { required: true, allowed_aggregations: ["count", "sum", "avg", "min", "max"] } },
    { type: "table", label: "Data Table", description: "Render raw records in tabular form.", x_axis: { required: true, allowed_bucket_types: ["terms", "date_histogram"] }, y_axis: { required: true, allowed_aggregations: ["count", "sum", "avg"] }, split_series: { supported: true } },
    { type: "horizontal_bar", label: "Horizontal Bar", description: "Display terms comparisons as horizontal bars.", x_axis: { required: true, allowed_bucket_types: ["terms"] }, y_axis: { required: true } },
    { type: "gauge", label: "Gauge Chart", description: "Radial dial showing metrics ranges.", x_axis: null, y_axis: { required: true } },
    { type: "timeline", label: "Timeline", description: "Plot events chronologically.", x_axis: { required: true, allowed_bucket_types: ["date_histogram"] } },
    { type: "heatmap", label: "Heat Map", description: "Colored grid showing metrics density.", x_axis: { required: true, allowed_bucket_types: ["terms"] }, split_series: { supported: true } },
    { type: "gantt", label: "Gantt Chart", description: "Visualize task schedules or timelines.", x_axis: { required: true, allowed_bucket_types: ["terms"] }, split_series: { supported: true } },
    { type: "candlestick", label: "Candlestick (Trend Analysis)", description: "OHLC candlestick chart for trend analysis.", x_axis: { required: true, allowed_bucket_types: ["terms", "date_histogram"] }, y_axis: { required: true, allowed_aggregations: ["count", "sum", "avg", "min", "max"] } }
];

const COLORS = ["#f97316", "#3b82f6", "#10b981", "#a855f7", "#ef4444", "#06b6d4", "#eab308", "#ec4899", "#8b5cf6", "#14b8a6"];

interface CustomFieldDropdownProps {
    label: string;
    fields: EntityField[];
    selectedValue: string;
    onSelect: (value: string) => void;
    placeholder: string;
    disabled?: boolean;
}

function CustomFieldDropdown({
    label,
    fields,
    selectedValue,
    onSelect,
    placeholder,
    disabled = false
}: CustomFieldDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedField = fields.find(f => f.name === selectedValue);

    const filteredFields = fields.filter(f =>
        f.name.toLowerCase().includes(search.toLowerCase()) ||
        (f.label && f.label.toLowerCase().includes(search.toLowerCase())) ||
        f.type.toLowerCase().includes(search.toLowerCase())
    );

    const getTypeColor = (type: string) => {
        switch (type.toLowerCase()) {
            case "integer":
            case "float":
            case "number":
                return "bg-blue-500/10 text-blue-400 border-blue-500/20";
            case "string":
            case "varchar":
            case "text":
                return "bg-amber-500/10 text-amber-400 border-amber-500/20";
            case "datetime":
            case "date":
            case "timestamp":
                return "bg-purple-500/10 text-purple-400 border-purple-500/20";
            case "boolean":
            case "bool":
                return "bg-teal-500/10 text-teal-400 border-teal-500/20";
            default:
                return "bg-gray-500/10 text-gray-400 border-gray-500/20";
        }
    };

    return (
        <div className="flex flex-col gap-1.5 relative w-full" ref={dropdownRef}>
            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400 flex items-center gap-1">
                <Info size={11} className="text-orange-400" /> {label}
            </label>

            {/* Trigger Button */}
            <button
                type="button"
                disabled={disabled}
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full flex items-center justify-between bg-gray-950 border border-gray-700 rounded-lg px-3 py-2.5 text-xs focus:outline-none transition-colors text-left ${disabled
                    ? "opacity-40 cursor-not-allowed text-gray-500"
                    : "text-white hover:border-orange-500/50"
                    }`}
            >
                {selectedField ? (
                    <div className="flex items-center justify-between w-full pr-2">
                        <div className="flex flex-col">
                            <span className="font-bold text-white leading-normal">
                                {selectedField.label || selectedField.name}
                            </span>
                            {/* <span className="text-[9px] text-gray-500 font-mono">{selectedField.name}</span> */}
                        </div>
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase border ${getTypeColor(selectedField.type)}`}>
                            {selectedField.type}
                        </span>
                    </div>
                ) : (
                    <span className="text-gray-500">{placeholder}</span>
                )}
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 shrink-0 ml-2 ${isOpen ? "rotate-180" : ""}`} />
            </button>

            {/* Dropdown Popover */}
            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-1.5 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl z-50 flex flex-col max-h-48 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
                    {/* Search inside dropdown */}
                    <div className="p-2 border-b border-gray-800 shrink-0 relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                        <input
                            type="text"
                            placeholder="Search fields..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full bg-gray-950 border border-gray-800 rounded-md pl-8 pr-3 py-1.5 text-xs text-white focus:outline-none focus:border-orange-500 transition-colors placeholder-gray-600"
                        />
                    </div>

                    {/* Options List */}
                    <div className="flex-1 overflow-y-auto custom-scroll py-1">
                        {filteredFields.map((field) => {
                            const isSelected = field.name === selectedValue;
                            return (
                                <button
                                    type="button"
                                    key={field.name}
                                    onClick={() => {
                                        onSelect(field.name);
                                        setIsOpen(false);
                                        setSearch("");
                                    }}
                                    className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-orange-500/5 transition-colors ${isSelected ? "bg-orange-500/10" : ""
                                        }`}
                                >
                                    <div className="flex flex-col">
                                        <span className={`text-xs font-semibold ${isSelected ? "text-orange-400 font-bold" : "text-gray-300"}`}>
                                            {field.label || field.name}
                                        </span>
                                        {/* <span className="text-[9px] text-gray-500 font-mono">{field.name}</span> */}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase border ${getTypeColor(field.type)}`}>
                                            {field.type}
                                        </span>
                                        {isSelected && <Check className="w-3.5 h-3.5 text-orange-500" />}
                                    </div>
                                </button>
                            );
                        })}
                        {filteredFields.length === 0 && (
                            <div className="px-3 py-4 text-center text-xs text-gray-600">
                                No matching fields.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// Custom Select Component for Options Selection
interface CustomSelectOption {
    value: string;
    label: string;
    description?: string;
}

interface CustomSelectProps {
    label: string;
    options: CustomSelectOption[];
    selectedValue: string;
    onSelect: (value: string) => void;
    placeholder?: string;
}

function CustomSelect({
    label,
    options,
    selectedValue,
    onSelect,
    placeholder = "Select option..."
}: CustomSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedOption = options.find(o => o.value === selectedValue);

    return (
        <div className="flex flex-col gap-1.5 relative w-full" ref={dropdownRef}>
            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400 flex items-center gap-1">
                <Info size={11} className="text-orange-400" /> {label}
            </label>

            {/* Trigger Button */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between bg-gray-950 border border-gray-700 hover:border-orange-500/50 rounded-lg px-3 py-2.5 text-xs text-white focus:outline-none transition-colors text-left font-semibold"
            >
                {selectedOption ? (
                    <div className="flex flex-col">
                        <span className="font-bold text-white leading-normal">
                            {selectedOption.label}
                        </span>
                        {/* {selectedOption.description && (
                            <span className="text-[9px] text-gray-500 leading-tight font-normal">{selectedOption.description}</span>
                        )} */}
                    </div>
                ) : (
                    <span className="text-gray-500">{placeholder}</span>
                )}
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 shrink-0 ml-2 ${isOpen ? "rotate-180" : ""}`} />
            </button>

            {/* Dropdown Popover */}
            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-1.5 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl z-50 flex flex-col max-h-48 overflow-y-auto custom-scroll py-1 animate-in fade-in slide-in-from-top-1 duration-150">
                    {options.map((option) => {
                        const isSelected = option.value === selectedValue;
                        return (
                            <button
                                type="button"
                                key={option.value}
                                onClick={() => {
                                    onSelect(option.value);
                                    setIsOpen(false);
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-orange-500/5 transition-colors ${isSelected ? "bg-orange-500/10" : ""
                                    }`}
                            >
                                <div className="flex flex-col min-w-0 pr-2">
                                    <span className={`text-xs font-semibold ${isSelected ? "text-orange-400 font-bold" : "text-gray-300"}`}>
                                        {option.label}
                                    </span>
                                    {option.description && (
                                        <span className="text-[9px] text-gray-500 leading-tight font-normal">{option.description}</span>
                                    )}
                                </div>
                                {isSelected && <Check className="w-3.5 h-3.5 text-orange-500 shrink-0" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default function VisualizePage() {
    const navigate = useNavigate();
    const location = useLocation();

    // Edit state
    const [editWidgetId, setEditWidgetId] = useState<string | null>(null);

    // Delete states
    const [deleteWidgetId, setDeleteWidgetId] = useState<string | null>(null);
    const [deleteWidgetName, setDeleteWidgetName] = useState<string>("");
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const showCreateModal = location.pathname === "/custom-dashboard/visualize/create" || !!editWidgetId;
    const level = useClientLevel();
    const clientId = level.id;

    // Metadata States
    const [chartTypeConfigs, setChartTypeConfigs] = useState<any[]>([]);
    const [aggregations, setAggregations] = useState<any[]>([]);
    const [bucketTypes, setBucketTypes] = useState<any[]>([]);
    const [loadingMetadata, setLoadingMetadata] = useState(true);

    // Navigation and Filtering States
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [filterText, setFilterText] = useState("");

    // Pagination States
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    // Step 1: Chart Type Selection States
    const [selectedType, setSelectedType] = useState<VisType | null>(null);

    // Step 2: Entity Selection States
    const [entities, setEntities] = useState<EntityItem[]>([]);
    const [loadingEntities, setLoadingEntities] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState<EntityItem | null>(null);

    // Step 3: Configuration & Axes States
    const [fields, setFields] = useState<EntityField[]>([]);
    const [loadingFields, setLoadingFields] = useState(false);
    const [selectedXField, setSelectedXField] = useState("");
    const [selectedYField, setSelectedYField] = useState("");




    // Widget Details State
    const [widgetName, setWidgetName] = useState("");
    const [widgetDescription, setWidgetDescription] = useState("");

    // Axes Configuration parameters
    const [xAxisBucketType, setXAxisBucketType] = useState("terms");
    const [xAxisSize, setXAxisSize] = useState(10);
    const [xAxisInterval, setXAxisInterval] = useState("day");
    const [xAxisLabel, setXAxisLabel] = useState("");

    const [yAxisAggregation, setYAxisAggregation] = useState("count");
    const [yAxisLabel, setYAxisLabel] = useState("Count");

    // Y-Axis Bucket aggregation states (for Heatmap)
    const [yAxisBucketType, setYAxisBucketType] = useState("terms");
    const [yAxisSize, setYAxisSize] = useState(10);
    const [yAxisInterval, setYAxisInterval] = useState("day");

    const [splitSeriesFields, setSplitSeriesFields] = useState<string[]>([]);

    // Widgets list states
    const [widgets, setWidgets] = useState<any[]>([]);
    const [loadingWidgets, setLoadingWidgets] = useState(false);

    // Preview / Query states
    const [previewResult, setPreviewResult] = useState<QueryResponse | null>(null);
    const [busy, setBusy] = useState(false);

    // Preview Widget modal states
    const [previewWidgetId, setPreviewWidgetId] = useState<string | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const [thresholdShow, setThresholdShow] = useState(false);
    const [thresholdValue, setThresholdValue] = useState(0);
    const [thresholdColor, setThresholdColor] = useState("#E7664C");
    const [colorPalette, setColorPalette] = useState<string[]>([]);

    // Clear Y-Axis field if aggregation is count
    useEffect(() => {
        if (yAxisAggregation === "count") {
            setSelectedYField("");
        }
    }, [yAxisAggregation]);

    // Fetch initial workspace metadata on load
    useEffect(() => {
        const fetchWorkspaceMetadata = async () => {
            setLoadingMetadata(true);
            try {
                const [chartTypesRes, aggregationsRes, bucketTypesRes] = await Promise.all([
                    baseapi.get("/analytics/chart-types"),
                    baseapi.get("/analytics/aggregations"),
                    baseapi.get("/analytics/bucket-types")
                ]);

                if (chartTypesRes.data?.status) setChartTypeConfigs(chartTypesRes.data.data || []);
                if (aggregationsRes.data?.status) setAggregations(aggregationsRes.data.data || []);
                if (bucketTypesRes.data?.status) setBucketTypes(bucketTypesRes.data.data || []);
            } catch (error: any) {
                console.error("Failed to load analytics metadata, using local fallbacks", error);
                setChartTypeConfigs(DEFAULT_CHART_CONFIGS);
                setAggregations([
                    { type: "count", label: "Count", requires_field: false, description: "Counts number of documents/records." },
                    { type: "sum", label: "Sum", requires_field: true, description: "Calculates sum of a numeric field." },
                    { type: "avg", label: "Average", requires_field: true, description: "Calculates average of a numeric field." },
                    { type: "min", label: "Min", requires_field: true, description: "Finds minimum value of a field." },
                    { type: "max", label: "Max", requires_field: true, description: "Finds maximum value of a field." }
                ]);
                setBucketTypes([
                    { type: "terms", label: "Terms", description: "Groups by unique values of a field." },
                    { type: "date_histogram", label: "Date Histogram", description: "Groups by time intervals of a datetime field." }
                ]);
            } finally {
                setLoadingMetadata(false);
            }
        };

        fetchWorkspaceMetadata();
    }, []);

    const fetchWidgets = async (skip = (currentPage - 1) * itemsPerPage, limit = itemsPerPage) => {
        setLoadingWidgets(true);
        try {
            const res = await baseapi.get("/analytics/widgets", {
                params: {
                    query: JSON.stringify({ client_id: clientId }),
                    skip,
                    limit
                }
            });
            const list = res.data?.data !== undefined ? res.data.data : (Array.isArray(res.data) ? res.data : []);
            setWidgets(list);
            const total = res.data?.total !== undefined ? res.data.total : list.length;
            setTotalItems(total);
            setTotalPages(Math.ceil(total / limit));
        } catch (error) {
            console.error("Failed to fetch widgets", error);
        } finally {
            setLoadingWidgets(false);
        }
    };

    useEffect(() => {
        if (!showCreateModal) {
            setCurrentPage(1);
            fetchWidgets(0, itemsPerPage);
        }
    }, [clientId, showCreateModal]);

    const handlePageChange = (page: number): void => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            fetchWidgets((page - 1) * itemsPerPage, itemsPerPage);
        }
    };

    const handleItemsPerPageChange = (value: number): void => {
        setItemsPerPage(value);
        setCurrentPage(1);
        fetchWidgets(0, value);
    };

    const handleClose = () => {
        setStep(1);
        setSelectedType(null);
        setSelectedEntity(null);
        setFields([]);
        setSelectedXField("");
        setSelectedYField("");
        setSplitSeriesFields([]);
        setPreviewResult(null);
        setFilterText("");
        setEditWidgetId(null);
        setWidgetName("");
        setWidgetDescription("");
        setXAxisBucketType("terms");
        setXAxisSize(10);
        setXAxisInterval("day");
        setXAxisLabel("");
        setYAxisAggregation("count");
        setYAxisLabel("Count");
        setYAxisBucketType("terms");
        setYAxisSize(10);
        setYAxisInterval("day");
        setThresholdShow(false);
        setThresholdValue(0);
        setThresholdColor("#E7664C");
        setColorPalette([]);
        navigate("/custom-dashboard/visualize");
    };

    const handleEditClick = async (widgetId: string) => {
        setBusy(true);
        try {
            // Fetch widget details
            const res = await baseapi.get(`/analytics/widgets/${widgetId}`, {
                params: { client_id: clientId }
            });
            const w = res.data?.data || res.data;
            if (!w) {
                showError("Failed to fetch widget details");
                return;
            }

            // Set basic details
            setEditWidgetId(widgetId);
            setWidgetName(w.name || "");
            setWidgetDescription(w.description || "");

            // Find matching visual type
            const matchingType = VISUALIZATION_TYPES.find(t => t.id === w.chart_type) || null;
            setSelectedType(matchingType);

            // Load entity list to match entity info
            const entitiesRes = await baseapi.get("/analytics/entities");
            const entitiesList = (entitiesRes.data && entitiesRes.data.status) ? (entitiesRes.data.data || []) : [];
            setEntities(entitiesList);

            let q = w.query_definition;
            if (q && typeof q === "string") {
                try {
                    q = JSON.parse(q);
                } catch (e) {
                    console.error("Failed to parse query_definition:", e);
                }
            }

            if (q) {
                const sourceEntity = entitiesList.find((e: any) => e.entity === q.source) || null;
                setSelectedEntity(sourceEntity);

                if (q.source) {
                    // Fetch schema fields for the entity
                    setLoadingFields(true);
                    const fieldsRes = await baseapi.get(`/analytics/entities/${q.source}`);
                    if (fieldsRes.data && fieldsRes.data.status) {
                        const nextFields = fieldsRes.data.data?.fields || [];
                        setFields(nextFields);
                    }
                    setLoadingFields(false);
                }

                // X-Axis
                const xAxis = q.axis?.x_axis;
                if (xAxis) {
                    setSelectedXField(xAxis.field || "");
                    setXAxisBucketType(xAxis.type || "terms");
                    if (xAxis.type === "terms") {
                        setXAxisSize(xAxis.size || 10);
                    } else if (xAxis.type === "date_histogram") {
                        setXAxisInterval(xAxis.interval || "day");
                    }
                }

                // Y-Axis
                if (w.chart_type === "gantt") {
                    const xAxis = q.axis?.x_axis;
                    if (xAxis) {
                        setSelectedXField(xAxis.field || "");
                        setXAxisSize(xAxis.size || 20);
                    }
                    const startY = q.axis?.y_axis?.[0];
                    if (startY) {
                        setSelectedYField(startY.field || "");
                    }
                    const endY = q.axis?.y_axis?.[1];
                    if (endY) {
                        setSplitSeriesFields([endY.field || ""]);
                    }
                } else if (w.chart_type === "heatmap" || w.chart_type === "heat_map") {
                    const split = q.axis?.split_series;
                    if (split) {
                        setYAxisBucketType(split.type || "terms");
                        setSelectedYField(split.field || "");
                        if (split.type === "terms") {
                            setYAxisSize(split.size || 10);
                        } else if (split.type === "date_histogram") {
                            setYAxisInterval(split.interval || "day");
                        }
                    }
                    setYAxisLabel(w.visualization_config?.y_axis_label || "Y-Axis");
                } else {
                    const yAxis = q.axis?.y_axis?.[0];
                    if (yAxis) {
                        setYAxisAggregation(yAxis.type || "count");
                        setSelectedYField(yAxis.field || "");
                        setYAxisLabel(yAxis.label || "Count");
                    }
                }

                // Split Series
                const split = q.axis?.split_series;
                if (Array.isArray(split)) {
                    setSplitSeriesFields(split.map((s: any) => s.field || "").filter(Boolean));
                } else if (split && typeof split === "object") {
                    setSplitSeriesFields(split.field ? [split.field] : []);
                } else {
                    setSplitSeriesFields([]);
                }
            }

            // Set visual configs
            let visConfig = w.visualization_config;
            if (visConfig && typeof visConfig === "string") {
                try {
                    visConfig = JSON.parse(visConfig);
                } catch (e) {
                    console.error("Failed to parse visualization_config:", e);
                }
            }
            if (visConfig) {
                setXAxisLabel(visConfig.x_axis_label || "");
                setYAxisLabel(visConfig.y_axis_label || "Count");
                setColorPalette(visConfig.color_palette || []);
                if (visConfig.threshold) {
                    setThresholdShow(!!visConfig.threshold.show);
                    setThresholdValue(Number(visConfig.threshold.value || 0));
                    setThresholdColor(visConfig.threshold.color || "#E7664C");
                } else {
                    setThresholdShow(false);
                    setThresholdValue(0);
                    setThresholdColor("#E7664C");
                }
            }

            // Set step to 3
            setStep(3);

            // Execute Query to trigger preview
            if (q) {
                const queryRes = await baseapi.post("/analytics/query", q, {
                    params: { client_id: clientId }
                });
                const queryData = queryRes.data?.data !== undefined ? queryRes.data.data : queryRes.data;
                setPreviewResult(queryData);
            }
        } catch (error: any) {
            console.error("Failed to load widget for editing:", error);
            showError(error.message || "Failed to load widget for editing");
        } finally {
            setBusy(false);
        }
    };

    // Transition from Step 1 to Step 2: Load Entities
    const handleNextToStep2 = async (type: VisType) => {
        setSelectedType(type);
        setStep(2);
        setFilterText("");
        setLoadingEntities(true);

        // Pre-validate bucket type based on new chart type configs
        const configs = chartTypeConfigs.length ? chartTypeConfigs : DEFAULT_CHART_CONFIGS;
        const config = configs.find(c => c.type === type.id);
        const allowedBuckets = config?.x_axis?.allowed_bucket_types || [];
        if (allowedBuckets.length > 0) {
            setXAxisBucketType(allowedBuckets[0]);
        }

        try {
            const response = await baseapi.get("/analytics/entities");
            if (response.data && response.data.status) {
                setEntities(response.data.data || []);
            } else {
                showError("Failed to fetch entities");
            }
        } catch (error: any) {
            showError(error.message || "Failed to load database tables");
        } finally {
            setLoadingEntities(false);
        }
    };

    // Transition from Step 2 to Step 3: Load Entity Fields
    const handleNextToStep3 = async (entity: EntityItem) => {
        setSelectedEntity(entity);
        setStep(3);
        setLoadingFields(true);
        setFields([]);
        setSelectedXField("");
        setSelectedYField("");
        setSplitSeriesFields([]);
        setPreviewResult(null);

        try {
            const response = await baseapi.get(`/analytics/entities/${entity.entity}`);
            if (response.data && response.data.status) {
                const nextFields = response.data.data?.fields || [];
                setFields(nextFields);

                // Auto-select first groupable field for X-axis
                const groupable = nextFields.find((f: any) => f.is_groupable && !f.is_jsonb && f.name !== "id");
                if (groupable) setSelectedXField(groupable.name);
            } else {
                showError("Failed to fetch entity fields");
            }
        } catch (error: any) {
            showError(error.message || "Failed to load table fields");
        } finally {
            setLoadingFields(false);
        }
    };

    // Current chart config mapping — resolve aliases so e.g. "vertical_bar" → "bar" config
    const currentChartConfig = useMemo(() => {
        const list = chartTypeConfigs.length ? chartTypeConfigs : DEFAULT_CHART_CONFIGS;
        const typeId = selectedType?.id || "bar";
        // Try exact match first, then alias fallback
        return list.find(c => c.type === typeId)
            || list.find(c => c.type === (CHART_TYPE_ALIAS[typeId] || typeId))
            || null;
    }, [chartTypeConfigs, selectedType]);

    // Allowed configurations
    const allowedAggregations = useMemo(() => {
        if (!currentChartConfig?.y_axis) return [];
        return currentChartConfig.y_axis.allowed_aggregations || [];
    }, [currentChartConfig]);

    const allowedBucketTypes = useMemo(() => {
        if (!currentChartConfig?.x_axis) return [];
        return currentChartConfig.x_axis.allowed_bucket_types || [];
    }, [currentChartConfig]);

    const aggregationRequiresField = useMemo(() => {
        const agg = aggregations.find(a => a.type === yAxisAggregation);
        return agg?.requires_field ?? (yAxisAggregation !== "count");
    }, [aggregations, yAxisAggregation]);


    const yAxisAggregationOptions = useMemo(() => {
        return aggregations
            .filter(a => allowedAggregations.length === 0 || allowedAggregations.includes(a.type))
            .map(agg => ({
                value: agg.type,
                label: agg.label,
                description: agg.description
            }));
    }, [aggregations, allowedAggregations]);

    const xAxisAggregationOptions = useMemo(() => {
        return bucketTypes
            .filter(b => allowedBucketTypes.length === 0 || allowedBucketTypes.includes(b.type))
            .map(bucket => ({
                value: bucket.type,
                label: bucket.label,
                description: bucket.description
            }));
    }, [bucketTypes, allowedBucketTypes]);

    const intervalOptions = useMemo(() => [
        { value: "hour", label: "Hourly" },
        { value: "day", label: "Daily" },
        { value: "week", label: "Weekly" },
        { value: "month", label: "Monthly" },
        { value: "year", label: "Yearly" }
    ], []);

    // Query JSON builder logic
    const queryDefinition = useMemo(() => {
        if (!selectedEntity) return null;

        const query: any = {
            source: selectedEntity.entity,
            filters: [],
            limit: 100,
        };

        const axis: any = {};

        // Y-Axis (Metric Aggregation or Y-Bucket for Heatmap)
        if (selectedType?.id === "gantt") {
            if (selectedXField) {
                axis.x_axis = {
                    type: "terms",
                    field: selectedXField,
                    size: xAxisSize || 20,
                    order_by: "_count",
                    order_direction: "desc"
                };
            }
            axis.y_axis = [
                { type: "min", field: selectedYField || "", label: "Start Date" },
                { type: "max", field: splitSeriesFields[0] || selectedYField || "", label: "End Date" }
            ];
        } else if (selectedType?.id === "heatmap") {
            if (selectedYField) {
                const split: any = {
                    type: yAxisBucketType,
                    field: selectedYField,
                };
                if (yAxisBucketType === "terms") {
                    split.size = yAxisSize;
                    split.order_by = "_count";
                    split.order_direction = "desc";
                } else if (yAxisBucketType === "date_histogram") {
                    split.interval = yAxisInterval;
                }
                axis.split_series = split;
            }
            axis.y_axis = [{ type: "count", label: yAxisLabel || "Count" }];
        } else {
            const metric: any = { type: yAxisAggregation };
            if (aggregationRequiresField && selectedYField) {
                metric.field = selectedYField;
            }
            if (yAxisLabel) {
                metric.label = yAxisLabel;
            }
            axis.y_axis = [metric];
        }

        // X-Axis (skipped for gantt)
        if (selectedType?.id !== "gantt" && currentChartConfig?.x_axis && selectedXField) {
            const effectiveBucketType = allowedBucketTypes.length === 0 || allowedBucketTypes.includes(xAxisBucketType)
                ? xAxisBucketType
                : allowedBucketTypes[0];

            const xAxis: any = {
                type: effectiveBucketType,
                field: selectedXField,
            };
            if (effectiveBucketType === "terms") {
                xAxis.size = xAxisSize;
                xAxis.order_by = "_count";
                xAxis.order_direction = "desc";
            }
            if (effectiveBucketType === "date_histogram") {
                xAxis.interval = xAxisInterval;
            }
            axis.x_axis = xAxis;
        }

        // Split Series (only if not heatmap and not gantt)
        if (selectedType?.id !== "heatmap" && selectedType?.id !== "gantt" && currentChartConfig?.split_series?.supported && splitSeriesFields.length > 0 && splitSeriesFields[0]) {
            axis.split_series = {
                type: "terms",
                field: splitSeriesFields[0],
                size: 5
            };
        }

        query.axis = axis;
        return query;
    }, [
        selectedEntity,
        yAxisAggregation,
        selectedYField,
        yAxisLabel,
        aggregationRequiresField,
        currentChartConfig,
        xAxisBucketType,
        selectedXField,
        xAxisSize,
        xAxisInterval,
        allowedBucketTypes,
        splitSeriesFields,
        selectedType,
        yAxisBucketType,
        yAxisSize,
        yAxisInterval
    ]);

    // Live execution of visualization query
    const executeQuery = async () => {
        if (!queryDefinition) return;
        setBusy(true);
        try {
            const response = await baseapi.post("/analytics/query", queryDefinition, {
                params: { client_id: clientId }
            });
            if (response.data) {
                const resultData = response.data.data !== undefined ? response.data.data : response.data;
                setPreviewResult(resultData);
                showSuccess("Query preview generated successfully!");
            }
        } catch (error: any) {
            showError(error.message || "Failed to execute query preview");
            setPreviewResult(null);
        } finally {
            setBusy(false);
        }
    };

    // Save widget creation flow
    const handleCreateVisualization = async () => {
        if (!queryDefinition) return;
        setBusy(true);
        try {
            const payload = {
                name: widgetName,
                description: widgetDescription,
                chart_type: selectedType?.id || "bar",
                query_definition: queryDefinition,
                visualization_config: {
                    x_axis_label: xAxisLabel || selectedXField || "X-Axis",
                    y_axis_label: yAxisLabel || "Count",
                    show_legend: true,
                    color_palette: colorPalette,          // ← add
                    threshold: {                           // ← add
                        show: thresholdShow,
                        value: thresholdValue,
                        color: thresholdColor,
                        line_style: "full",
                        label: "",
                    },
                },
                refresh_interval: 0
            };
            console.log("threshold", payload);

            const requestParams = { client_id: clientId };

            let widgetRes;
            if (editWidgetId) {
                // PUT request for editing widget
                widgetRes = await baseapi.put(`/analytics/widgets/${editWidgetId}`, payload, {
                    params: requestParams
                });
            } else {
                // POST request for creating widget
                widgetRes = await baseapi.post("/analytics/widgets", payload, {
                    params: requestParams
                });
            }

            if (widgetRes.status === 200 || widgetRes.status === 201 || widgetRes.data?.status) {
                showSuccess(editWidgetId ? "Widget updated successfully!" : "Widget saved successfully!");
                handleClose();
                fetchWidgets((currentPage - 1) * itemsPerPage, itemsPerPage);
                navigate("/custom-dashboard/visualize");
            } else {
                showError("Failed to save widget");
            }
        } catch (error: any) {
            showError(error.message || "Failed to save widget");
        } finally {
            setBusy(false);
        }
    };

    const handleDeleteSubmit = async () => {
        if (!deleteWidgetId) return;
        setDeleting(true);
        try {
            const res = await baseapi.delete(`/analytics/widgets/${deleteWidgetId}`, {
                params: { client_id: clientId }
            });
            if (res.status === 200 || res.status === 204 || res.data?.status) {
                showSuccess("Widget deleted successfully!");
                setIsDeleteConfirmOpen(false);
                setDeleteWidgetId(null);
                setDeleteWidgetName("");
                let nextPage = currentPage;
                if (widgets.length === 1 && currentPage > 1) {
                    nextPage = currentPage - 1;
                    setCurrentPage(nextPage);
                }
                fetchWidgets((nextPage - 1) * itemsPerPage, itemsPerPage);
            } else {
                showError("Failed to delete widget");
            }
        } catch (error: any) {
            console.error("Failed to delete widget:", error);
            showError(error.message || "Failed to delete widget");
        } finally {
            setDeleting(false);
        }
    };

    // Filter elements based on search query in Step 1 & Step 2
    const filteredTypes = VISUALIZATION_TYPES.filter(type =>
        type.name.toLowerCase().includes(filterText.toLowerCase())
    );

    const filteredEntities = entities.filter(item =>
        item.entity.toLowerCase().includes(filterText.toLowerCase()) ||
        item.category.toLowerCase().includes(filterText.toLowerCase()) ||
        item.table_name.toLowerCase().includes(filterText.toLowerCase())
    );

    if (loadingMetadata) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-900">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                    <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">Loading Workspace...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-[calc(100vh-90px)] text-gray-200 relative p-2 overflow-hidden">
            {/* Tabs Bar */}
            <div className="flex border-b border-gray-700 mb-3 gap-2 shrink-0">
                <button
                    onClick={() => navigate("/custom-dashboard")}
                    className="px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-800/20 transition-all rounded-t-md"
                >
                    Dashboards
                </button>
                <button
                    onClick={() => navigate("/custom-dashboard/visualize")}
                    className="px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 text-white border-orange-500 bg-gray-800/40 rounded-t-md transition-all"
                >
                    Widgets
                </button>
            </div>

            {/* Header section */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-3 border-b border-gray-700 pb-3">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-500/10 rounded mr-2.5">
                        <Eye className="w-4 h-4 text-orange-400" />
                    </div>
                    <div className="flex flex-col">
                        <h2 className="text-lg font-bold text-white leading-tight">Visualize</h2>
                        <p className="text-gray-200 text-[11px] uppercase font-semibold tracking-wider">
                            Visualization Workspace
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {!showCreateModal && (
                        <>
                            <button
                                onClick={() => fetchWidgets((currentPage - 1) * itemsPerPage, itemsPerPage)}
                                disabled={loadingWidgets}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-200 text-xs font-semibold rounded-lg transition-colors border border-gray-700 hover:text-white disabled:opacity-50 cursor-pointer"
                            >
                                <RefreshCw size={14} className={loadingWidgets ? "animate-spin" : ""} /> Refresh
                            </button>
                            <button
                                onClick={() => navigate("/custom-dashboard/visualize/create")}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white text-xs font-semibold rounded-lg transition-colors border border-orange-500"
                            >
                                <Plus size={14} /> Add
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Content area: empty state placeholder or full page wizard */}
            {!showCreateModal ? (
                loadingWidgets ? (
                    <div className="bg-gray-900/40 rounded-md border border-gray-700 overflow-hidden flex flex-col justify-center items-center p-16 text-center flex-1">
                        <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin mb-3"></div>
                        <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">Loading Widgets...</span>
                    </div>
                ) : widgets.length === 0 ? (
                    <div className="bg-gray-900/40 rounded-md border border-gray-700 overflow-hidden flex flex-col justify-center items-center p-16 text-center flex-1">
                        <div className="p-4 bg-orange-500/10 rounded-full mb-4">
                            <Eye className="w-8 h-8 text-orange-400" />
                        </div>
                        <h3 className="text-sm font-bold text-white mb-1 uppercase tracking-wider">Visualization Workspace</h3>
                        <p className="text-xs text-gray-400 max-w-sm mb-6">
                            Configure and customize your metrics visualization boards. Click the "Add" button to build a new visualization chart.
                        </p>
                        <button
                            onClick={() => navigate("/custom-dashboard/visualize/create")}
                            className="flex items-center gap-1.5 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-xs font-semibold rounded-lg transition-colors border border-gray-700 hover:text-white"
                        >
                            <Plus size={14} className="text-orange-400" /> Create first chart
                        </button>
                    </div>
                ) : (
                    <div className="bg-gray-900/40 rounded-md border border-gray-700 overflow-hidden flex flex-col relative">
                        <div className="flex-1 overflow-x-auto overflow-y-auto custom-scroll">
                            <table className="w-full table-fixed text-left min-w-[600px]">
                                <colgroup>
                                    <col className="w-[20%]" />
                                    <col className="w-[30%]" />
                                    <col className="w-[15%]" />
                                    <col className="w-[20%]" />
                                    <col className="w-[15%]" />
                                </colgroup>
                                <thead className="bg-gray-800 sticky top-0 z-10">
                                    <tr className="border-b border-gray-700">
                                        <th className="px-3 py-2 text-[10px] font-bold text-gray-300 uppercase tracking-wider">Name</th>
                                        <th className="px-3 py-2 text-[10px] font-bold text-gray-300 uppercase tracking-wider">Description</th>
                                        <th className="px-3 py-2 text-[10px] font-bold text-gray-300 uppercase tracking-wider">Chart Type</th>
                                        <th className="px-3 py-2 text-[10px] font-bold text-gray-300 uppercase tracking-wider">Updated</th>
                                        <th className="px-3 py-2 text-[10px] font-bold text-gray-300 uppercase tracking-wider text-right pr-6">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-800/50">
                                    {widgets.map((w) => (
                                        <tr key={w.id} className="hover:bg-gray-800/40 transition-colors duration-100">
                                            <td className="px-3 py-2">
                                                <span className="text-[12px] font-bold text-white truncate block" title={w.name}>
                                                    {w.name}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className="text-[11px] text-gray-300 truncate block font-normal" title={w.description}>
                                                    {w.description || "-"}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-orange-500/10 text-orange-400 border border-orange-500/20 inline-block">
                                                    {w.chart_type}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className="text-[10px] text-gray-300">
                                                    {w.updated ? new Date(w.updated).toLocaleString() : "-"}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-right pr-6">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setPreviewWidgetId(w.id);
                                                            setIsPreviewOpen(true);
                                                        }}
                                                        title="Preview Widget"
                                                        className="p-1 hover:bg-orange-500/10 text-orange-400 hover:text-orange-500 rounded transition-colors"
                                                    >
                                                        <Eye size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEditClick(w.id)}
                                                        title="Edit Widget"
                                                        className="p-1 hover:bg-orange-500/10 text-orange-400 hover:text-orange-500 rounded transition-colors"
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setDeleteWidgetId(w.id);
                                                            setDeleteWidgetName(w.name || "");
                                                            setIsDeleteConfirmOpen(true);
                                                        }}
                                                        title="Delete Widget"
                                                        className="p-1 hover:bg-red-500/10 text-red-400 hover:text-red-500 rounded transition-colors"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {/* Pagination Footer */}
                        <div className="bg-gray-900/60 border-t border-gray-700 px-4 py-2 flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-4">
                                {/* <select
                                    className="px-2 py-1 bg-gray-800 border border-gray-700 rounded text-white text-[10px] focus:outline-none   cursor-pointer"
                                    value={itemsPerPage}
                                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                </select> */}
                                <ItemsPerPageDropdown
                                    value={itemsPerPage}
                                    onChange={handleItemsPerPageChange}
                                />
                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                                    <span className="text-gray-200">{totalItems === 0 ? 0 : startIndex + 1}</span>
                                    {" - "}
                                    <span className="text-gray-200">{Math.min(endIndex, totalItems)}</span>
                                    {" OF "}
                                    <span className="text-gray-200">{totalItems}</span>
                                </span>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => handlePageChange(1)}
                                    disabled={currentPage === 1}
                                    className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400 transition-colors cursor-pointer"
                                    title="First Page"
                                >
                                    <ChevronsLeft className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400 transition-colors cursor-pointer"
                                    title="Previous Page"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-950/20">
                                    {currentPage}
                                </div>
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages || totalPages === 0}
                                    className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400 transition-colors cursor-pointer"
                                    title="Next Page"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => handlePageChange(totalPages)}
                                    disabled={currentPage === totalPages || totalPages === 0}
                                    className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400 transition-colors cursor-pointer"
                                    title="Last Page"
                                >
                                    <ChevronsRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )
            ) : (
                <div className="bg-gray-900 border border-gray-700 rounded-xl flex-1 flex flex-col overflow-hidden shadow-2xl animate-in fade-in duration-200">

                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 shrink-0">
                        <div className="flex items-center gap-2">
                            <h3 className="text-md font-bold text-white tracking-wide">{editWidgetId ? "Edit Widget" : "New Widget"}</h3>
                            <span className="text-[10px] px-2 py-0.5 rounded bg-gray-800 text-orange-400 border border-gray-700 font-bold uppercase tracking-wider">
                                Step {step} of 3
                            </span>
                        </div>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-white p-1 hover:bg-gray-800 rounded transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Wizard Panels */}
                    <div className="flex flex-1 overflow-hidden">

                        {/* ================= STEP 1: Select Chart Type ================= */}
                        {step === 1 && (
                            <div className="w-full p-6 flex flex-col overflow-hidden">
                                <div className="relative mb-5 shrink-0">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                    <input
                                        type="text"
                                        placeholder="Filter visualizations..."
                                        value={filterText}
                                        onChange={(e) => setFilterText(e.target.value)}
                                        className="w-full bg-gray-950/60 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-xs text-white focus:outline-none focus:border-orange-500 transition-colors placeholder-gray-500"
                                    />
                                </div>

                                <div className="flex-1 overflow-y-auto custom-scroll pr-1">
                                    <div className="grid grid-cols-5 gap-2.5">
                                        {filteredTypes.map((type) => {
                                            const IconComponent = type.icon;
                                            const isSelected = selectedType?.id === type.id;
                                            return (
                                                <button
                                                    key={type.id}
                                                    onClick={() => handleNextToStep2(type)}
                                                    className={`flex flex-col items-center justify-center p-3 rounded-lg border text-center transition-all group ${isSelected
                                                        ? "border-orange-500 bg-orange-500/5"
                                                        : "border-gray-800 hover:border-orange-500/50 bg-gray-950/20 hover:bg-orange-500/5"
                                                        }`}
                                                >
                                                    <div className={`p-2 rounded-md transition-colors ${isSelected
                                                        ? "bg-orange-500/20"
                                                        : "bg-gray-800 group-hover:bg-orange-500/10"
                                                        } mb-2`}>
                                                        <IconComponent className={`w-4 h-4 ${isSelected ? "text-orange-400" : type.color
                                                            }`} />
                                                    </div>
                                                    <span className="text-[10px] font-bold tracking-tight text-gray-300 group-hover:text-white transition-colors">
                                                        {type.name}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {filteredTypes.length === 0 && (
                                        <div className="flex flex-col items-center justify-center h-48 text-gray-500 text-xs">
                                            No visualization types match your filter.
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ================= STEP 2: Select Table/Entity ================= */}
                        {step === 2 && (
                            <div className="w-full p-6 flex flex-col overflow-hidden">
                                <div className="flex items-center gap-3 mb-5 shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => setStep(1)}
                                        className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-bold rounded-lg transition-colors border border-gray-700 flex items-center gap-1.5 cursor-pointer"
                                    >
                                        <ArrowLeft size={12} /> Back
                                    </button>
                                    <div className="relative flex-1">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                        <input
                                            type="text"
                                            placeholder="Search entities or tables..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="w-full bg-gray-950/60 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-xs text-white focus:outline-none focus:border-orange-500 transition-colors placeholder-gray-500"
                                        />
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto custom-scroll pr-1">
                                    {loadingEntities ? (
                                        <div className="flex flex-col items-center justify-center h-48 space-y-3">
                                            <div className="w-6 h-6 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                                            <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">Loading Tables...</span>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-2">
                                            {filteredEntities.map((item) => {
                                                const isSelected = selectedEntity?.entity === item.entity;
                                                return (
                                                    <button
                                                        key={item.entity}
                                                        onClick={() => handleNextToStep3(item)}
                                                        className={`flex items-center justify-between p-3 rounded-lg border text-left transition-all ${isSelected
                                                            ? "border-orange-500 bg-orange-500/5"
                                                            : "border-gray-800 hover:border-orange-500/50 bg-gray-950/20 hover:bg-orange-500/5"
                                                            }`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className="p-2 rounded bg-gray-800 text-teal-400">
                                                                <Database className="w-4 h-4" />
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <span className="text-xs font-bold text-white leading-normal">{item.label}</span>
                                                                {/* <span className="text-[10px] text-gray-500 font-mono">{item.table_name}</span> */}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-gray-800 text-gray-400 border border-gray-700">
                                                                {item.category}
                                                            </span>
                                                            <span className="text-[10px] text-gray-400 font-medium">
                                                                {item.field_count} fields
                                                            </span>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                            {filteredEntities.length === 0 && (
                                                <div className="flex flex-col items-center justify-center h-48 text-gray-500 text-xs">
                                                    No entities match your search.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ================= STEP 3: Define Axes ================= */}
                        {step === 3 && (
                            <div className="w-full p-6 flex flex-col overflow-hidden h-full">
                                <div className="flex items-center justify-between mb-4 shrink-0">
                                    <div className="flex items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setStep(2)}
                                            className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-bold rounded-lg transition-colors border border-gray-700 flex items-center gap-1.5 cursor-pointer"
                                        >
                                            <ArrowLeft size={12} /> Back
                                        </button>
                                        <h4 className="text-sm font-bold text-white tracking-wide">Configure Visualization Settings</h4>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={executeQuery}
                                        disabled={busy || !selectedXField || (selectedType?.id === "heatmap" ? !selectedYField : (selectedType?.id === "gantt" ? (!selectedYField || !splitSeriesFields[0]) : (aggregationRequiresField && !selectedYField)))}
                                        className="px-3 py-1.5 bg-orange-600/10 hover:bg-orange-600/20 text-orange-400 font-bold border border-orange-500/20 rounded-lg text-xs transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                                    >
                                        <Play size={12} /> Preview Widget
                                    </button>
                                </div>

                                {loadingFields ? (
                                    <div className="flex-1 flex flex-col items-center justify-center h-full space-y-3">
                                        <div className="w-6 h-6 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                                        <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">Loading Schema...</span>
                                    </div>
                                ) : (
                                    <div className="flex-1 flex gap-6 overflow-hidden min-h-0">
                                        {/* Left Side: Preview Panel */}
                                        <div className="w-3/5 flex flex-col h-full gap-4">
                                            <div className="flex-1 border border-gray-800 bg-gray-950/20 rounded-xl p-6 flex flex-col justify-center items-center relative overflow-hidden">
                                                {busy ? (
                                                    <div className="flex flex-col items-center gap-3">
                                                        <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                                                        <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">Loading Preview...</span>
                                                    </div>
                                                ) : previewResult ? (
                                                    <div className="w-full h-full flex flex-col">
                                                        <div className="flex items-center justify-between pb-3 border-b border-gray-800 mb-3 shrink-0">
                                                            <span className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
                                                                <Eye className="w-4 h-4 text-orange-400" /> Preview: {widgetName}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => setPreviewResult(null)}
                                                                className="text-xs text-gray-400 hover:text-white"
                                                            >
                                                                ✕ Clear
                                                            </button>
                                                        </div>
                                                        <div className="flex-1 w-full min-h-0">
                                                            <DataViz
                                                                data={previewResult}
                                                                chartType={selectedType?.id || "bar"}
                                                                xAxisLabel={xAxisLabel || selectedXField || ""}
                                                                yAxisLabel={yAxisLabel || "Count"}
                                                                colorPalette={colorPalette}
                                                                onColorPaletteChange={setColorPalette}
                                                                threshold={{ show: thresholdShow, value: thresholdValue, color: thresholdColor }}
                                                            />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center p-8 flex flex-col items-center gap-3">
                                                        <div className="p-4 bg-orange-500/10 rounded-full">
                                                            <Play className="w-8 h-8 text-orange-400" />
                                                        </div>
                                                        <h4 className="text-sm font-bold text-white uppercase tracking-wider">No Preview Generated</h4>
                                                        <p className="text-xs text-gray-500 max-w-xs leading-relaxed">
                                                            Configure your metric aggregations and bucket groupings on the right, then click "Preview Widget" to verify your chart.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Right Side: Fields panel */}
                                        <div className="w-2/5 flex flex-col justify-between h-full overflow-y-auto custom-scroll pr-2 gap-6">
                                            <div className="flex flex-col gap-6">
                                                {/* Widget Details */}
                                                <div className="flex flex-col gap-4">
                                                    <div className="flex flex-col gap-1.5">
                                                        <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Widget Name</label>
                                                        <input
                                                            type="text"
                                                            value={widgetName}
                                                            onChange={e => setWidgetName(e.target.value)}
                                                            className="bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-orange-500 transition-colors"
                                                        />
                                                    </div>
                                                    <div className="flex flex-col gap-1.5">
                                                        <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Widget Description</label>
                                                        <input
                                                            type="text"
                                                            value={widgetDescription}
                                                            onChange={e => setWidgetDescription(e.target.value)}
                                                            className="bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-orange-500 transition-colors"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Y-Axis Configuration Card */}
                                                {selectedType?.id === "gantt" ? (
                                                    <>
                                                        {/* Gantt Row Labels Card */}
                                                        <div className="border border-gray-800 bg-gray-950/20 rounded-lg p-4">
                                                            <div className="flex items-center justify-between border-b border-gray-800 pb-2 mb-3">
                                                                <h4 className="text-[11px] font-bold text-white uppercase tracking-wide">Row Labels</h4>
                                                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-red-400 font-bold uppercase tracking-wider">Required</span>
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-4">
                                                                <div className="flex flex-col gap-1.5 col-span-2">
                                                                    <CustomFieldDropdown
                                                                        label="Task / Row Field"
                                                                        fields={fields}
                                                                        selectedValue={selectedXField}
                                                                        onSelect={setSelectedXField}
                                                                        placeholder="Select field (e.g. name, title)..."
                                                                    />
                                                                </div>
                                                                <div className="flex flex-col gap-1.5 col-span-2">
                                                                    <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Max Tasks (Size)</label>
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        max="100"
                                                                        value={xAxisSize}
                                                                        onChange={e => setXAxisSize(Number(e.target.value))}
                                                                        className="bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-orange-500 transition-colors"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Gantt Date Timeline Card */}
                                                        <div className="border border-gray-800 bg-gray-950/20 rounded-lg p-4">
                                                            <div className="flex items-center justify-between border-b border-gray-800 pb-2 mb-3">
                                                                <h4 className="text-[11px] font-bold text-white uppercase tracking-wide">Timeline Dates</h4>
                                                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-red-400 font-bold uppercase tracking-wider">Required</span>
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-4">
                                                                <div className="flex flex-col gap-1.5 col-span-2">
                                                                    <CustomFieldDropdown
                                                                        label="Start Date Field"
                                                                        fields={fields}
                                                                        selectedValue={selectedYField}
                                                                        onSelect={setSelectedYField}
                                                                        placeholder="Select start date field..."
                                                                    />
                                                                </div>
                                                                <div className="flex flex-col gap-1.5 col-span-2">
                                                                    <CustomFieldDropdown
                                                                        label="End Date Field"
                                                                        fields={fields}
                                                                        selectedValue={splitSeriesFields[0] || ""}
                                                                        onSelect={(val) => setSplitSeriesFields([val])}
                                                                        placeholder="Select end date field..."
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        {selectedType?.id === "heatmap" ? (
                                                            <div className="border border-gray-800 bg-gray-950/20 rounded-lg p-4">
                                                                <div className="flex items-center justify-between border-b border-gray-800 pb-2 mb-3">
                                                                    <h4 className="text-[11px] font-bold text-white uppercase tracking-wide">Buckets (Y-Axis)</h4>
                                                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-red-400 font-bold uppercase tracking-wider">Required</span>
                                                                </div>
                                                                <div className="grid grid-cols-2 gap-4">
                                                                    <div className="flex flex-col gap-1.5">
                                                                        <CustomSelect
                                                                            label="Aggregation"
                                                                            options={xAxisAggregationOptions}
                                                                            selectedValue={yAxisBucketType}
                                                                            onSelect={setYAxisBucketType}
                                                                        />
                                                                    </div>
                                                                    <div className="flex flex-col gap-1.5">
                                                                        <CustomFieldDropdown
                                                                            label="Field"
                                                                            fields={fields}
                                                                            selectedValue={selectedYField}
                                                                            onSelect={setSelectedYField}
                                                                            placeholder="Select Y-Axis grouping field..."
                                                                        />
                                                                    </div>
                                                                    <div className="flex flex-col gap-1.5">
                                                                        {yAxisBucketType === "terms" && (
                                                                            <div className="flex flex-col gap-1.5">
                                                                                <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Size (Top N)</label>
                                                                                <input
                                                                                    type="number"
                                                                                    min="1"
                                                                                    max="100"
                                                                                    value={yAxisSize}
                                                                                    onChange={e => setYAxisSize(Number(e.target.value))}
                                                                                    className="bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-orange-500 transition-colors"
                                                                                />
                                                                            </div>
                                                                        )}
                                                                        {yAxisBucketType === "date_histogram" && (
                                                                            <div className="flex flex-col gap-1.5">
                                                                                <CustomSelect
                                                                                    label="Interval"
                                                                                    options={intervalOptions}
                                                                                    selectedValue={yAxisInterval}
                                                                                    onSelect={setYAxisInterval}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex flex-col gap-1.5">
                                                                        <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Custom Label</label>
                                                                        <input
                                                                            type="text"
                                                                            value={yAxisLabel}
                                                                            onChange={e => setYAxisLabel(e.target.value)}
                                                                            placeholder="Y-Axis"
                                                                            className="bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-orange-500 transition-colors"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="border border-gray-800 bg-gray-950/20 rounded-lg p-4">
                                                                <div className="flex items-center justify-between border-b border-gray-800 pb-2 mb-3">
                                                                    <h4 className="text-[11px] font-bold text-white uppercase tracking-wide">Metrics (Y-Axis)</h4>
                                                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-red-400 font-bold uppercase tracking-wider">Required</span>
                                                                </div>
                                                                <div className="grid grid-cols-2 gap-4">
                                                                    <div className="flex flex-col gap-1.5">
                                                                        <CustomSelect
                                                                            label="Aggregation"
                                                                            options={yAxisAggregationOptions}
                                                                            selectedValue={yAxisAggregation}
                                                                            onSelect={setYAxisAggregation}
                                                                        />
                                                                    </div>
                                                                    <div className="flex flex-col gap-1.5">
                                                                        <CustomFieldDropdown
                                                                            label={`Field ${!aggregationRequiresField ? "(not required)" : ""}`}
                                                                            fields={selectedYField ? fields : fields.filter(f => f.is_aggregatable)}
                                                                            selectedValue={selectedYField}
                                                                            onSelect={setSelectedYField}
                                                                            placeholder={aggregationRequiresField ? "Select numeric field..." : "Aggregation field..."}
                                                                            disabled={yAxisAggregation === "count"}
                                                                        />
                                                                    </div>
                                                                    <div className="flex flex-col gap-1.5 col-span-2">
                                                                        <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Custom Label</label>
                                                                        <input
                                                                            type="text"
                                                                            value={yAxisLabel}
                                                                            onChange={e => setYAxisLabel(e.target.value)}
                                                                            placeholder="Count"
                                                                            className="bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-orange-500 transition-colors"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Buckets (X-Axis) Card */}
                                                        {currentChartConfig?.x_axis && (
                                                            <div className="border border-gray-800 bg-gray-950/20 rounded-lg p-4">
                                                                <div className="flex items-center justify-between border-b border-gray-800 pb-2 mb-3">
                                                                    <h4 className="text-[11px] font-bold text-white uppercase tracking-wide">Buckets (X-Axis)</h4>
                                                                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${currentChartConfig.x_axis.required
                                                                        ? "bg-red-500/10 border border-red-500/20 text-red-400"
                                                                        : "bg-teal-500/10 border border-teal-500/20 text-teal-400"
                                                                        }`}>
                                                                        {currentChartConfig.x_axis.required ? "Required" : "Optional"}
                                                                    </span>
                                                                </div>
                                                                <div className="grid grid-cols-2 gap-4">
                                                                    <div className="flex flex-col gap-1.5">
                                                                        <CustomSelect
                                                                            label="Aggregation"
                                                                            options={xAxisAggregationOptions}
                                                                            selectedValue={xAxisBucketType}
                                                                            onSelect={setXAxisBucketType}
                                                                        />
                                                                    </div>
                                                                    <div className="flex flex-col gap-1.5">
                                                                        <CustomFieldDropdown
                                                                            label="Field"
                                                                            fields={fields}
                                                                            selectedValue={selectedXField}
                                                                            onSelect={setSelectedXField}
                                                                            placeholder="Select X-Axis grouping field..."
                                                                        />
                                                                    </div>
                                                                    <div className="flex flex-col gap-1.5">
                                                                        {xAxisBucketType === "terms" && (
                                                                            <div className="flex flex-col gap-1.5">
                                                                                <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Size (Top N)</label>
                                                                                <input
                                                                                    type="number"
                                                                                    min="1"
                                                                                    max="100"
                                                                                    value={xAxisSize}
                                                                                    onChange={e => setXAxisSize(Number(e.target.value))}
                                                                                    className="bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-orange-500 transition-colors"
                                                                                />
                                                                            </div>
                                                                        )}
                                                                        {xAxisBucketType === "date_histogram" && (
                                                                            <div className="flex flex-col gap-1.5">
                                                                                <CustomSelect
                                                                                    label="Interval"
                                                                                    options={intervalOptions}
                                                                                    selectedValue={xAxisInterval}
                                                                                    onSelect={setXAxisInterval}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex flex-col gap-1.5">
                                                                        <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Custom Label</label>
                                                                        <input
                                                                            type="text"
                                                                            value={xAxisLabel}
                                                                            onChange={e => setXAxisLabel(e.target.value)}
                                                                            placeholder="X-Axis"
                                                                            className="bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-orange-500 transition-colors"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Split Series Card */}
                                                        {/* Split Series Card — only for charts that support it */}
                                                        {selectedType?.id !== "heatmap" && currentChartConfig?.split_series?.supported && (
                                                            <div className="border border-gray-800 bg-gray-950/20 rounded-lg p-4">
                                                                <div className="flex items-center justify-between border-b border-gray-800 pb-2 mb-3">
                                                                    <h4 className="text-[11px] font-bold text-white uppercase tracking-wide">
                                                                        {selectedType?.id === "table" || selectedType?.id === "table"
                                                                            ? "Grouping Columns"
                                                                            : "Split Series"}
                                                                    </h4>
                                                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-teal-500/10 border border-teal-500/20 text-teal-400 font-bold uppercase tracking-wider">
                                                                        Optional
                                                                    </span>
                                                                </div>
                                                                <div className="flex flex-col gap-3">
                                                                    {splitSeriesFields.map((field, idx) => (
                                                                        <div key={idx} className="flex items-end gap-2 bg-gray-900/40 p-2.5 rounded-lg border border-gray-800">
                                                                            <div className="flex-1 min-w-0">
                                                                                <CustomFieldDropdown
                                                                                    label={
                                                                                        selectedType?.id === "table" || selectedType?.id === "table"
                                                                                            ? `Grouping Column ${idx + 1}`
                                                                                            : `Split Field ${idx + 1}`
                                                                                    }
                                                                                    fields={fields.filter(
                                                                                        (f) =>
                                                                                            f.is_groupable &&
                                                                                            f.name !== selectedXField &&
                                                                                            (!splitSeriesFields.includes(f.name) || f.name === field)
                                                                                    )}
                                                                                    selectedValue={field}
                                                                                    onSelect={(val) => {
                                                                                        const next = [...splitSeriesFields];
                                                                                        next[idx] = val;
                                                                                        setSplitSeriesFields(next.filter(Boolean));
                                                                                    }}
                                                                                    placeholder="Select field"
                                                                                />
                                                                            </div>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() =>
                                                                                    setSplitSeriesFields(splitSeriesFields.filter((_, i) => i !== idx))
                                                                                }
                                                                                className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-lg text-xs transition-colors shrink-0"
                                                                                title="Remove field"
                                                                            >
                                                                                <X size={12} />
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setSplitSeriesFields([...splitSeriesFields, ""])}
                                                                        className="w-full py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                                                                    >
                                                                        <Plus size={12} />
                                                                        Add Split Field / Grouping Column
                                                                    </button>
                                                                    <p className="text-[10px] text-gray-500 italic">
                                                                        {currentChartConfig.split_series?.description ??
                                                                            "Divides primary chart values into multiple comparison lines/bars based on a secondary category field."}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* ✅ Colors & Threshold — show for ALL chart types EXCEPT heatmap/gantt/gauge/candlestick/table */}
                                                        {!["heatmap", "gantt", "gauge", "candlestick", "piechart"].includes(
                                                            selectedType?.id || ""
                                                        ) && (
                                                                <div className="border border-gray-800 bg-gray-950/20 rounded-lg p-4">
                                                                    <div className="flex items-center justify-between border-b border-gray-800 pb-2 mb-3">
                                                                        <h4 className="text-[11px] font-bold text-white uppercase tracking-wide">
                                                                            Colors &amp; Threshold
                                                                        </h4>
                                                                    </div>

                                                                    {/* ── Color Palette ── */}
                                                                    <div className="flex flex-col gap-2 mb-4">
                                                                        <div className="flex items-center justify-between">
                                                                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">
                                                                                Color Palette
                                                                            </label>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    const nextColor = COLORS[colorPalette.length % COLORS.length] || "#f97316";
                                                                                    setColorPalette([...colorPalette, nextColor]);
                                                                                }}
                                                                                className="text-[10px] text-orange-400 hover:text-orange-300 font-semibold flex items-center gap-1 transition-colors"
                                                                            >
                                                                                <Plus size={10} />
                                                                                Add
                                                                            </button>
                                                                        </div>

                                                                        <div className="flex flex-col gap-2">
                                                                            {colorPalette.map((color, idx) => (
                                                                                <div key={idx} className="flex items-end gap-2">
                                                                                    <div className="flex-1">
                                                                                        <ColorPickerInput
                                                                                            label={`Series ${idx + 1}`}
                                                                                            value={color}
                                                                                            onChange={(hex) => {
                                                                                                const next = [...colorPalette];
                                                                                                next[idx] = hex;
                                                                                                setColorPalette(next);
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                    {colorPalette.length > 0 && (
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() =>
                                                                                                setColorPalette(colorPalette.filter((_, i) => i !== idx))
                                                                                            }
                                                                                            className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-lg transition-colors shrink-0"
                                                                                        >
                                                                                            <X size={12} />
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>

                                                                    {/* ── Threshold ── */}
                                                                    <div className="border-t border-gray-800 pt-3">
                                                                        <div className="flex items-center justify-between mb-3">
                                                                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">
                                                                                Threshold Line
                                                                            </label>
                                                                            {/* Toggle */}
                                                                            <div
                                                                                onClick={() => setThresholdShow((v) => !v)}
                                                                                className={`relative w-8 h-4 rounded-full border cursor-pointer transition-colors ${thresholdShow
                                                                                    ? "bg-orange-600 border-orange-500"
                                                                                    : "bg-gray-800 border-gray-700"
                                                                                    }`}
                                                                            >
                                                                                <div
                                                                                    className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${thresholdShow ? "translate-x-4" : "translate-x-0.5"
                                                                                        }`}
                                                                                />
                                                                            </div>
                                                                        </div>

                                                                        <div className="grid grid-cols-2 gap-3">
                                                                            {/* Value */}
                                                                            <div className="flex flex-col gap-1.5">
                                                                                <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">
                                                                                    Value
                                                                                </label>
                                                                                <input
                                                                                    type="number"
                                                                                    value={thresholdValue}
                                                                                    onChange={(e) => setThresholdValue(Number(e.target.value))}
                                                                                    disabled={!thresholdShow}
                                                                                    className="bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-orange-500 transition-colors disabled:opacity-40"
                                                                                />
                                                                            </div>

                                                                            {/* Color */}
                                                                            <ColorPickerInput
                                                                                label="Color"
                                                                                value={thresholdColor}
                                                                                onChange={setThresholdColor}
                                                                                disabled={!thresholdShow}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                    </>
                                                )}

                                                {/* Generated Query Preview */}
                                                <details className="group border border-gray-800 rounded-lg overflow-hidden bg-gray-950/10 shrink-0">
                                                    <summary className="px-4 py-2 text-[10px] uppercase font-bold text-gray-500 cursor-pointer select-none bg-gray-950/20 hover:text-white transition-colors">
                                                        View Generated Query JSON
                                                    </summary>
                                                    <pre className="p-3 text-[10px] text-gray-400 font-mono overflow-auto max-h-42 custom-scroll bg-gray-950/40 border-t border-gray-900">
                                                        {queryDefinition ? JSON.stringify(queryDefinition, null, 2) : "Select configurations to generate query structure..."}
                                                    </pre>
                                                </details>
                                            </div>

                                            {/* Action Buttons for Step 3 */}
                                            <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-gray-800 shrink-0 pb-2">
                                                <button
                                                    type="button"
                                                    onClick={handleCreateVisualization}
                                                    disabled={busy || !selectedXField || (selectedType?.id === "heatmap" ? !selectedYField : (selectedType?.id === "gantt" ? (!selectedYField || !splitSeriesFields[0]) : (aggregationRequiresField && !selectedYField)))}
                                                    className="w-full py-2.5 bg-orange-600 hover:bg-orange-500 disabled:bg-gray-800 text-white disabled:text-gray-500 text-xs font-bold rounded-lg transition-colors border border-orange-500 disabled:border-gray-800 flex items-center justify-center gap-1.5 disabled:cursor-not-allowed cursor-pointer"
                                                >
                                                    <Save size={12} /> {editWidgetId ? "Save Changes" : "Create Visualization"}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
            <PreviewWidgetModal
                isOpen={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                widgetId={previewWidgetId}
                clientId={clientId}
            />
            <DeleteConfirmModal
                isOpen={isDeleteConfirmOpen}
                onClose={() => setIsDeleteConfirmOpen(false)}
                onConfirm={handleDeleteSubmit}
                itemName={deleteWidgetName}
                itemType="widget"
                busy={deleting}
            />
        </div>
    );
}

// Data pivoting and parsing functions
function pivotData(data: QueryResponse | null) {
    if (!data) return null;
    const columns = data.columns || [];
    const rows = data.rows || [];
    if (columns.length !== 3 || rows.length === 0) return data;

    // Check if third column is numeric
    const valueIsNumeric = rows.some(r => Number.isFinite(Number(r[2])));
    if (!valueIsNumeric) return data;

    // Check for repeated col[0] values (indicates flat pivoting layout)
    const col0Keys = rows.map(r => String(r[0] ?? ""));
    const uniqueDims = new Set(col0Keys);
    if (uniqueDims.size === rows.length) return data;

    // Collect unique categories from col[1]
    const allCategories: string[] = [];
    const catSeen = new Set<string>();
    for (const row of rows) {
        const cat = String(row[1] ?? "");
        if (!catSeen.has(cat)) {
            catSeen.add(cat);
            allCategories.push(cat);
        }
    }

    // Limit to top 10 categories to keep visualization readable
    const categorySet = allCategories.slice(0, 10);

    // Collect unique dimension values (col[0]) in order
    const dimOrder: any[] = [];
    const dimSeen = new Set<string>();
    for (const row of rows) {
        const key = String(row[0] ?? "");
        if (!dimSeen.has(key)) {
            dimSeen.add(key);
            dimOrder.push(row[0]);
        }
    }

    // Build lookup: dimKey -> categoryKey -> value
    const lookup: Record<string, Record<string, number>> = {};
    for (const row of rows) {
        const dimKey = String(row[0] ?? "");
        const cat = String(row[1] ?? "");
        if (!lookup[dimKey]) lookup[dimKey] = {};
        lookup[dimKey][cat] = Number(row[2]) || 0;
    }

    const newColumns = [columns[0], ...categorySet];
    const newRows = dimOrder.map(dim => {
        const dimKey = String(dim ?? "");
        return [dim, ...categorySet.map(cat => lookup[dimKey]?.[cat] ?? 0)];
    });

    return {
        columns: newColumns,
        rows: newRows,
        _pivoted: true,
        _categoryLabel: columns[1],
        _metricLabel: columns[2]
    };
}

// ─── Gauge SVG Component ────────────────────────────────────────────────────
function GaugeChart({ value, label, max: propMax }: { value: number; label?: string; max?: number }) {
    const displayValue = typeof value === "number" ? value : 0;

    // Auto-calculate a round max if not supplied
    const max = propMax || (() => {
        if (displayValue <= 0) return 100;
        const mag = Math.pow(10, Math.floor(Math.log10(Math.max(displayValue, 1))));
        return Math.ceil(displayValue / mag) * mag * 2;
    })();

    const pct = Math.min(Math.max(displayValue / max, 0), 1);

    const W = 280, H = 175;
    const cx = W / 2, cy = 152;
    const R = 108;
    const trackW = 22;

    // Convert pct (0=left, 1=right, through top) to SVG point
    const toXY = (r: number, p: number): [number, number] => {
        const a = Math.PI * (1 - p); // π→0 as p→1
        return [cx + r * Math.cos(a), cy - r * Math.sin(a)];
    };

    // SVG arc string from p1→p2 clockwise on screen (through top = arch shape)
    const arc = (r: number, p1: number, p2: number, sweep = 1) => {
        const [x1, y1] = toXY(r, p1);
        const [x2, y2] = toXY(r, p2);
        const large = (p2 - p1) > 0.5 ? 1 : 0;
        return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} ${sweep} ${x2.toFixed(2)} ${y2.toFixed(2)}`;
    };

    const zoneColor = (p: number) =>
        p < 0.4 ? "#0d9488" : p < 0.7 ? "#f59e0b" : "#ef4444";

    // 21 ticks across the arc
    const ticks = Array.from({ length: 21 }, (_, i) => i / 20);

    // Left / right endpoint labels
    const [lx, ly] = toXY(R + trackW / 2 + 14, 0);
    const [rx2, ry2] = toXY(R + trackW / 2 + 14, 1);

    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" style={{ maxHeight: 200 }}>
            {/* Background track */}
            <path d={arc(R, 0, 1)} fill="none" stroke="#1f2937" strokeWidth={trackW} strokeLinecap="round" />

            {/* Dimmed zone bands */}
            <path d={arc(R, 0, 0.4)} fill="none" stroke="#0d9488" strokeWidth={trackW} opacity={0.18} />
            <path d={arc(R, 0.4, 0.7)} fill="none" stroke="#f59e0b" strokeWidth={trackW} opacity={0.18} />
            <path d={arc(R, 0.7, 1)} fill="none" stroke="#ef4444" strokeWidth={trackW} opacity={0.18} />

            {/* Value arc */}
            {pct > 0 && (
                <path d={arc(R, 0, pct)} fill="none" stroke={zoneColor(pct)} strokeWidth={trackW} strokeLinecap="round" />
            )}

            {/* Zone boundary lines at 40% and 70% */}
            {[0.4, 0.7].map((p) => {
                const [ox, oy] = toXY(R - trackW / 2 - 1, p);
                const [ix, iy] = toXY(R + trackW / 2 + 1, p);
                return <line key={p} x1={ox} y1={oy} x2={ix} y2={iy} stroke="#0f172a" strokeWidth={2} />;
            })}

            {/* Tick marks */}
            {ticks.map((t, i) => {
                const [ox, oy] = toXY(R - trackW / 2 + 2, t);
                const [ix, iy] = toXY(R - trackW / 2 + (i % 5 === 0 ? 10 : 6), t);
                return <line key={i} x1={ox} y1={oy} x2={ix} y2={iy} stroke="#374151" strokeWidth={i % 5 === 0 ? 2 : 1} />;
            })}

            {/* Center: label + value */}
            <text x={cx} y={cy - 16} textAnchor="middle" fill="#6b7280" fontSize={11} fontWeight={500}>
                {label || "Value"}
            </text>
            <text x={cx} y={cy + 14} textAnchor="middle" fill={zoneColor(pct)} fontSize={28} fontWeight={700} fontFamily="monospace">
                {displayValue.toLocaleString()}
            </text>

            {/* Min / Max labels */}
            <text x={lx} y={ly + 4} textAnchor="middle" fill="#4b5563" fontSize={9}>0</text>
            <text x={rx2} y={ry2 + 4} textAnchor="middle" fill="#4b5563" fontSize={9}>{max.toLocaleString()}</text>
        </svg>
    );
}

interface GanttChartProps {
    pivoted: any;
    xAxisLabel?: string;
    yAxisLabel?: string;
}

function GanttChart({ pivoted, xAxisLabel, yAxisLabel }: GanttChartProps) {
    if (!pivoted) return <div className="text-gray-500 text-xs p-4">No data returned.</div>;

    const columns = pivoted.columns || [];
    const rows = pivoted.rows || [];

    if (columns.length === 0 || rows.length === 0) {
        return <div className="text-gray-500 text-xs p-4">No data available.</div>;
    }

    const { tasks, timelineStart, timelineEnd } = useMemo(() => {
        const list: { name: string; start: number; end: number }[] = [];
        let absoluteMin = Infinity;
        let absoluteMax = -Infinity;

        rows.forEach((row: any) => {
            const name = String(row[0] ?? "Unnamed Task");
            let start = new Date(row[1]).getTime();
            let end = new Date(row[2]).getTime();

            if (isNaN(start)) start = Date.now();
            if (isNaN(end)) end = start + 86400000; // default to +1 day

            if (end < start) end = start + 86400000; // ensure positive duration

            if (start < absoluteMin) absoluteMin = start;
            if (end > absoluteMax) absoluteMax = end;

            list.push({ name, start, end });
        });

        // Fallbacks if empty or invalid
        if (absoluteMin === Infinity) absoluteMin = Date.now();
        if (absoluteMax === -Infinity) absoluteMax = Date.now() + 30 * 24 * 60 * 60 * 1000;

        // Pad timeline a bit (e.g. 5% of range)
        const range = absoluteMax - absoluteMin;
        const padding = range > 0 ? range * 0.05 : 24 * 60 * 60 * 1000;
        const timelineStart = absoluteMin - padding;
        const timelineEnd = absoluteMax + padding;

        return { tasks: list, timelineStart, timelineEnd };
    }, [rows]);

    const numIntervals = 6;
    const timeIntervals = useMemo(() => {
        const arr = [];
        const step = (timelineEnd - timelineStart) / (numIntervals - 1 || 1);
        for (let i = 0; i < numIntervals; i++) {
            const time = timelineStart + i * step;
            arr.push(new Date(time));
        }
        return arr;
    }, [timelineStart, timelineEnd]);

    const formatDate = (date: Date) => {
        return date.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric"
        });
    };

    const duration = timelineEnd - timelineStart;

    return (
        <div className="flex flex-col h-[380px] w-full select-none justify-between p-2 bg-gray-950/20 border border-gray-800 rounded-xl overflow-hidden">
            {/* Gantt Header */}
            <div className="flex border-b border-gray-800 bg-gray-900/40 select-none text-[9px] font-bold text-gray-400 py-1.5 shrink-0">
                <div className="w-[120px] pl-3">Task</div>
                <div className="flex-1 relative flex justify-between px-2">
                    {timeIntervals.map((date, idx) => (
                        <span key={idx} className="-translate-x-1/2 text-center select-none first:translate-x-0 last:translate-x-[-100%]">
                            {formatDate(date)}
                        </span>
                    ))}
                </div>
            </div>

            {/* Gantt Body */}
            <div className="flex-1 overflow-y-auto custom-scroll min-h-0 relative py-1 divide-y divide-gray-900/30">
                {tasks.map((task, idx) => {
                    const leftPct = ((task.start - timelineStart) / duration) * 100;
                    const widthPct = ((task.end - task.start) / duration) * 100;

                    return (
                        <div key={idx} className="flex items-center min-h-[32px] group py-1">
                            {/* Task Name */}
                            <div className="w-[120px] text-[10px] font-bold text-gray-300 truncate pr-2 pl-3 select-none" title={task.name}>
                                {task.name}
                            </div>

                            {/* Timeline Bar Space */}
                            <div className="flex-1 relative h-6 border-l border-gray-800/40 bg-gray-900/5 flex items-center">
                                {/* Grid lines background overlay */}
                                <div className="absolute inset-0 flex justify-between pointer-events-none">
                                    {timeIntervals.map((_, gIdx) => (
                                        <div key={gIdx} className="h-full border-l border-gray-800/30" />
                                    ))}
                                </div>

                                {/* Gantt Bar */}
                                <div
                                    className="absolute h-3.5 rounded bg-gradient-to-r from-orange-500 to-orange-600 border border-orange-400/30 flex items-center justify-center text-[8px] font-bold text-white shadow-md cursor-pointer hover:scale-[1.02] transition-all hover:shadow-orange-500/10"
                                    style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                                    title={`${task.name}: ${new Date(task.start).toLocaleString()} - ${new Date(task.end).toLocaleString()}`}
                                >
                                    {widthPct > 12 && (
                                        <span className="px-1 truncate font-mono">
                                            {((task.end - task.start) / (24 * 60 * 60 * 1000)).toFixed(1)}d
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Gantt Footer */}
            <div className="flex items-center justify-between border-t border-gray-800 pt-2 mt-2 text-[10px] text-gray-400 font-medium px-1 shrink-0">
                <div className="flex items-center gap-4">
                    {xAxisLabel && (
                        <span>
                            <strong className="text-gray-500">Row Label Field:</strong> {xAxisLabel}
                        </span>
                    )}
                    {yAxisLabel && (
                        <span>
                            <strong className="text-gray-500">Timeline Field:</strong> {yAxisLabel}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="text-gray-500 font-bold uppercase tracking-wider text-[8px]">Time Range</span>
                    <span className="text-[9px] font-semibold text-orange-400 font-mono">
                        {new Date(timelineStart).toLocaleDateString()} - {new Date(timelineEnd).toLocaleDateString()}
                    </span>
                </div>
            </div>
        </div>
    );
}

interface HeatmapChartProps {
    pivoted: any;
    xAxisLabel?: string;
    yAxisLabel?: string;
}

function HeatmapChart({ pivoted, xAxisLabel, yAxisLabel }: HeatmapChartProps) {
    if (!pivoted) return <div className="text-gray-500 text-xs p-4">No data returned.</div>;

    const columns = pivoted.columns || [];
    const rows = pivoted.rows || [];

    if (columns.length === 0 || rows.length === 0) {
        return <div className="text-gray-500 text-xs p-4">No data available.</div>;
    }

    const hasSplit = columns.length > 2;
    const yCategories = hasSplit ? columns.slice(1) : [columns[1] || "Value"];
    const xCategories = rows.map((r: any) => String(r[0] ?? ""));

    const maxVal = (() => {
        let max = 0;
        rows.forEach((row: any) => {
            const values = hasSplit ? row.slice(1) : [row[1]];
            values.forEach((v: any) => {
                const val = Number(v) || 0;
                if (val > max) max = val;
            });
        });
        return max || 1;
    })();

    const [hoveredCell, setHoveredCell] = useState<{
        xVal: string;
        yVal: string;
        value: number;
        x: number;
        y: number;
    } | null>(null);

    return (
        <div className="flex flex-col h-[380px] w-full select-none justify-between p-2">
            <div className="relative flex-1 flex min-h-0 min-w-0 gap-4 mt-2">
                <div className="flex flex-col justify-around text-right pr-2 select-none min-w-[70px] max-w-[120px] truncate text-[10px] font-semibold text-gray-400">
                    {xCategories.map((xCat: any) => (
                        <div key={xCat} className="truncate py-1" title={String(xCat)}>
                            {String(xCat)}
                        </div>
                    ))}
                </div>

                <div className="flex-1 flex flex-col min-w-0">
                    <div className="flex-1 flex flex-col justify-around gap-1.5 min-h-0">
                        {xCategories.map((xCat: any, xIdx: number) => (
                            <div key={xCat} className="flex-1 flex gap-1.5 min-h-0">
                                {yCategories.map((yCat: any, yIdx: number) => {
                                    const rawVal = hasSplit ? rows[xIdx][yIdx + 1] : rows[xIdx][1];
                                    const value = Number(rawVal) || 0;
                                    const pct = maxVal > 0 ? value / maxVal : 0;

                                    const cellStyle = {
                                        backgroundColor: value > 0
                                            ? `rgba(249, 115, 22, ${0.12 + pct * 0.88})`
                                            : "rgba(31, 41, 55, 0.25)",
                                        border: value > 0
                                            ? `1px solid rgba(249, 115, 22, ${0.2 + pct * 0.6})`
                                            : "1px solid rgba(75, 85, 99, 0.2)"
                                    };

                                    return (
                                        <div
                                            key={String(yCat)}
                                            className="flex-1 rounded-md cursor-pointer transition-all duration-150 hover:scale-[1.03] hover:shadow-lg relative flex items-center justify-center group"
                                            style={cellStyle}
                                            onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                const parentEl = e.currentTarget.closest(".relative.flex-1.flex");
                                                const parentRect = parentEl?.getBoundingClientRect();
                                                if (parentRect) {
                                                    setHoveredCell({
                                                        xVal: String(yCat),
                                                        yVal: xCat,
                                                        value,
                                                        x: rect.left - parentRect.left + rect.width / 2,
                                                        y: rect.top - parentRect.top - 8
                                                    });
                                                }
                                            }}
                                            onMouseLeave={() => setHoveredCell(null)}
                                        >
                                            {value > 0 && (
                                                <span className={`text-[10px] font-bold select-none ${pct > 0.5 ? "text-white" : "text-orange-400"}`}>
                                                    {value}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                    <div className="flex gap-1.5 pt-2 select-none h-6 items-center">
                        {yCategories.map((yCat: any) => (
                            <div key={yCat} className="flex-1 text-center text-[10px] font-semibold text-gray-400 truncate px-0.5" title={String(yCat)}>
                                {String(yCat)}
                            </div>
                        ))}
                    </div>
                </div>

                {hoveredCell && (
                    <div
                        className="absolute z-50 pointer-events-none bg-gray-950 border border-gray-700/80 rounded-lg p-2.5 shadow-2xl text-[11px] text-white flex flex-col gap-1 -translate-x-1/2 -translate-y-full transition-all duration-75 animate-in fade-in zoom-in-95 duration-100"
                        style={{ left: hoveredCell.x, top: hoveredCell.y }}
                    >
                        <div className="flex items-center gap-1.5 border-b border-gray-800 pb-1 mb-1 font-semibold">
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                            <span className="text-gray-300">Cell Details</span>
                        </div>
                        <div>
                            <span className="text-gray-500 font-medium">Bucket:</span> <span className="font-semibold">{hoveredCell.yVal}</span>
                        </div>
                        {hasSplit && (
                            <div>
                                <span className="text-gray-550 font-medium">Split Series:</span> <span className="font-semibold">{hoveredCell.xVal}</span>
                            </div>
                        )}
                        <div>
                            <span className="text-gray-500 font-medium">Value:</span> <span className="font-bold text-orange-400 font-mono text-xs">{hoveredCell.value.toLocaleString()}</span>
                        </div>
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between border-t border-gray-800 pt-2.5 mt-2 text-[10px] text-gray-400 font-medium px-1">
                <div className="flex items-center gap-4">
                    {xAxisLabel && (
                        <span>
                            <strong className="text-gray-500">X-Axis Field:</strong> {xAxisLabel}
                        </span>
                    )}
                    {yAxisLabel && (
                        <span>
                            <strong className="text-gray-500">Y-Axis Metric:</strong> {yAxisLabel}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="text-gray-500 font-bold uppercase tracking-wider text-[8px]">Density</span>
                    <span className="w-2.5 h-2.5 rounded bg-gray-900 border border-gray-800" />
                    <span className="text-[9px] text-gray-500">0</span>
                    <div className="h-2 w-16 rounded bg-gradient-to-r from-orange-500/20 to-orange-500 border border-orange-500/30" />
                    <span className="text-[9px] font-bold text-orange-400 font-mono">{maxVal.toLocaleString()}</span>
                </div>
            </div>
        </div>
    );
}



// const CustomActiveDot = (props: any) => {
//     const { cx, cy, stroke } = props;
//     if (!cx || !cy) return null;
//     return (
//         <g>
//             <circle cx={cx} cy={cy} r={10} fill={stroke} fillOpacity={0.25} />
//             <circle cx={cx} cy={cy} r={4.5} fill={stroke} stroke="#ffffff" strokeWidth={1.5} />
//         </g>
//     );
// };

interface DataVizProps {
    data: QueryResponse | null;
    chartType: string;
    xAxisLabel?: string;
    yAxisLabel?: string;
    colorPalette?: string[];
    onColorPaletteChange?: (colors: string[]) => void;
    threshold?: {
        show: boolean;
        value: number;
        color: string;
    };
}

function DataViz({ data, chartType, xAxisLabel: rawXAxisLabel, yAxisLabel: rawYAxisLabel, colorPalette, onColorPaletteChange, threshold }: DataVizProps) {
    const xAxisLabel = formatFieldName(rawXAxisLabel || "");
    const yAxisLabel = formatFieldName(rawYAxisLabel || "");
    // State inside DataViz
    const [dotTooltip, setDotTooltip] = useState<{
        x: number;
        y: number;
        name: string;
        value: number;
        color: string;
    } | null>(null);

    console.log("color palette", colorPalette);

    const pivoted = useMemo(() => {
        if (chartType === "table" || chartType === "table" || chartType === "gantt" || chartType === "candlestick") return data;
        return pivotData(data);
    }, [data, chartType]);
    if (!pivoted) return <div className="text-gray-500 text-xs p-4">No data returned.</div>;
    const ACTIVE_COLORS = (colorPalette && colorPalette.length > 0) ? colorPalette : COLORS;
    const columns = pivoted.columns || [];
    const rows = pivoted.rows || [];
    if (!columns.length || !rows.length) return <div className="text-gray-500 text-xs p-4">No rows returned.</div>;
    const toHex = (c: string) => (c.startsWith("#") ? c : `#${c}`);
    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const filtered = payload;

            if (!filtered.length) return null;

            return (
                <div className="bg-gray-800/95 backdrop-blur-md border border-gray-700/50 rounded-lg p-2.5 shadow-2xl text-[11px] text-white flex flex-col gap-1.5 min-w-[180px]">
                    {filtered.map((entry: any, index: number) => (
                        <div key={index} className="flex justify-between items-center gap-6">
                            <span className="flex items-center gap-1.5 text-gray-300 font-medium">
                                <span
                                    className="inline-block w-2 h-2 rounded-full shrink-0"
                                    style={{ backgroundColor: entry.color || entry.stroke }}
                                />
                                {formatFieldName(entry.name)}
                            </span>
                            <span className="font-bold text-white font-mono">
                                {entry.value?.toLocaleString()}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };
    const makeActiveDot = (col: string, color: string) => (props: any) => {
        const { cx, cy, value } = props;
        if (!cx || !cy) return null;
        return (
            <g
                onMouseEnter={() => setDotTooltip({ x: cx, y: cy, name: formatFieldName(col), value, color })}
                onMouseLeave={() => setDotTooltip(null)}
                style={{ cursor: 'pointer' }}
            >
                <circle cx={cx} cy={cy} r={12} fill={color} fillOpacity={0} />
                <circle cx={cx} cy={cy} r={10} fill={color} fillOpacity={0.25} />
                <circle cx={cx} cy={cy} r={4.5} fill={color} stroke="#ffffff" strokeWidth={1.5} />
            </g>
        );
    };


    // Table view
    if (chartType === "table" || chartType === "table") {
        return (
            <div className="overflow-auto border border-gray-700 rounded-lg h-[380px] custom-scroll">
                <table className="w-full text-left text-xs border-collapse">
                    <thead className="bg-gray-800/80 sticky top-0 text-gray-400 font-semibold border-b border-gray-700">
                        <tr>
                            {columns.map(col => <th key={col} className="px-3 py-2">{col}</th>)}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800 bg-gray-950/20">
                        {rows.map((row, rIdx) => (
                            <tr key={rIdx} className="hover:bg-gray-800/20 transition-colors">
                                {columns.map((_, cIdx) => (
                                    <td key={cIdx} className="px-3 py-2 text-gray-300">
                                        {formatCell(row[cIdx])}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    // Gantt View
    if (chartType === "gantt") {
        return (
            <GanttChart pivoted={pivoted} xAxisLabel={xAxisLabel} yAxisLabel={yAxisLabel} />
        );
    }

    // Heatmap View
    if (chartType === "heatmap" || chartType === "heat_map") {
        return (
            <HeatmapChart pivoted={pivoted} xAxisLabel={xAxisLabel} yAxisLabel={yAxisLabel} />
        );
    }

    // Candlestick (Trend Analysis) View
    if (chartType === "candlestick") {
        // Try to detect OHLC columns by name, fallback to positional
        const colsLower = columns.map((c: string) => (c || "").toLowerCase());
        const oIdx = colsLower.findIndex((c: string) => c.includes("open"));
        const hIdx = colsLower.findIndex((c: string) => c.includes("high"));
        const lIdx = colsLower.findIndex((c: string) => c.includes("low"));
        const cIdx = colsLower.findIndex((c: string) => c.includes("close"));
        const vIdx = colsLower.findIndex((c: string) => c.includes("vol"));
        const hasOHLC = oIdx >= 0 && hIdx >= 0 && lIdx >= 0 && cIdx >= 0;

        interface CandleItem { label: string; open: number; high: number; low: number; close: number; volume?: number; }
        let candles: CandleItem[];

        if (hasOHLC) {
            candles = rows.map((row: any) => ({
                label: String(row[0] ?? ""),
                open: Number(row[oIdx]) || 0,
                high: Number(row[hIdx]) || 0,
                low: Number(row[lIdx]) || 0,
                close: Number(row[cIdx]) || 0,
                volume: vIdx >= 0 ? Number(row[vIdx]) || 0 : undefined,
            }));
        } else if (columns.length >= 5) {
            // Positional: [label, open, high, low, close, ?volume]
            candles = rows.map((row: any) => ({
                label: String(row[0] ?? ""),
                open: Number(row[1]) || 0,
                high: Number(row[2]) || 0,
                low: Number(row[3]) || 0,
                close: Number(row[4]) || 0,
                volume: row[5] != null ? Number(row[5]) || 0 : undefined,
            }));
        } else {
            // Not enough columns — generate dummy candlestick from available data
            candles = rows.map((row: any) => {
                const val = Number(row[1]) || Number(row[0]) || 0;
                const spread = Math.max(val * 0.1, 1);
                return {
                    label: String(row[0] ?? ""),
                    open: val - spread * 0.3,
                    high: val + spread,
                    low: val - spread,
                    close: val + spread * 0.3,
                };
            });
        }

        if (candles.length === 0) {
            return <div className="text-gray-500 text-xs p-4">No candlestick data available.</div>;
        }

        // Use a large viewBox so text renders at readable sizes
        const W = 800, H = 400;
        const MG = { top: 20, right: 60, bottom: 80, left: 50 };
        const chartW = W - MG.left - MG.right;
        const chartH = H - MG.top - MG.bottom;

        const allHighs = candles.map(c => c.high);
        const allLows = candles.map(c => c.low);
        const dataMin = Math.min(...allLows);
        const dataMax = Math.max(...allHighs);
        const range = dataMax - dataMin || 1;
        const pad = range * 0.08;
        const yMin = dataMin - pad;
        const yRange = (dataMax + pad) - yMin;

        // Y-axis ticks (5 ticks)
        const yTicks: number[] = [];
        { const s = yRange / 4; for (let i = 0; i <= 4; i++) yTicks.push(yMin + s * i); }

        // Label sampling — show at most ~12 labels to avoid overlap
        const maxLabels = 12;
        const labelStep = Math.max(1, Math.ceil(candles.length / maxLabels));

        const count = candles.length;
        const gap = chartW / count;
        const candleW = Math.min(gap * 0.6, 20);

        const toY = (val: number) => MG.top + (1 - (val - yMin) / yRange) * chartH;

        return (
            <div className="h-[380px] w-full p-2">
                <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="w-full h-full">
                    {/* Chart background */}
                    <rect x={MG.left} y={MG.top} width={chartW} height={chartH} fill="#0f172a" rx="4" />

                    {/* Y-Axis grid lines + labels */}
                    {yTicks.map((tick, i) => {
                        const y = toY(tick);
                        return (
                            <g key={`yt-${i}`}>
                                <line x1={MG.left} y1={y} x2={MG.left + chartW} y2={y} stroke="#1e293b" strokeWidth="1" strokeDasharray="4 4" />
                                <text x={MG.left - 8} y={y + 4} fill="#6b7280" fontSize="11" fontFamily="monospace" textAnchor="end">
                                    {tick.toFixed(tick >= 1000 ? 0 : 1)}
                                </text>
                            </g>
                        );
                    })}

                    {/* Candles */}
                    {candles.map((candle, idx) => {
                        const cx = MG.left + gap * idx + gap / 2;
                        const isBullish = candle.close >= candle.open;
                        const bodyColor = isBullish ? "#22c55e" : "#ef4444";
                        const wickColor = isBullish ? "#16a34a" : "#dc2626";

                        const yHigh = toY(candle.high);
                        const yLow = toY(candle.low);
                        const yOpen = toY(candle.open);
                        const yClose = toY(candle.close);
                        const bodyTop = Math.min(yOpen, yClose);
                        const bodyHeight = Math.max(Math.abs(yOpen - yClose), 2);

                        return (
                            <g key={`c-${idx}`}>
                                {/* Wick */}
                                <line x1={cx} y1={yHigh} x2={cx} y2={yLow} stroke={wickColor} strokeWidth="1.5" />
                                {/* Body */}
                                <rect x={cx - candleW / 2} y={bodyTop} width={candleW} height={bodyHeight} fill={bodyColor} rx="1" stroke={wickColor} strokeWidth="0.5" />
                                {/* Hover area */}
                                <rect x={cx - gap / 2} y={MG.top} width={gap} height={chartH} fill="transparent">
                                    <title>{`${candle.label}\nO: ${candle.open}  H: ${candle.high}\nL: ${candle.low}  C: ${candle.close}${candle.volume !== undefined ? `\nVol: ${candle.volume.toLocaleString()}` : ""}`}</title>
                                </rect>
                                {/* X-axis label (sampled + rotated) */}
                                {idx % labelStep === 0 && (
                                    <text
                                        x={cx} y={MG.top + chartH + 14}
                                        fill="#9ca3af" fontSize="10" textAnchor="end" fontFamily="sans-serif"
                                        transform={`rotate(-45, ${cx}, ${MG.top + chartH + 14})`}
                                    >
                                        {candle.label.length > 12 ? candle.label.slice(0, 12) + "…" : candle.label}
                                    </text>
                                )}
                            </g>
                        );
                    })}

                    {/* Chart border */}
                    <line x1={MG.left} y1={MG.top} x2={MG.left} y2={MG.top + chartH} stroke="#374151" strokeWidth="1" />
                    <line x1={MG.left} y1={MG.top + chartH} x2={MG.left + chartW} y2={MG.top + chartH} stroke="#374151" strokeWidth="1" />
                </svg>
                {/* Legend */}
                <div className="flex items-center justify-between px-1 mt-1">
                    <div className="flex items-center gap-3 text-[10px] text-gray-400">
                        <div className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-green-500" /><span>Bullish</span></div>
                        <div className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-500" /><span>Bearish</span></div>
                    </div>
                    <div className="flex items-center gap-2 text-[9px] text-gray-500">
                        {xAxisLabel && <span>X: {xAxisLabel}</span>}
                        {yAxisLabel && <span>Y: {yAxisLabel}</span>}
                    </div>
                </div>
            </div>
        );
    }

    // Gauge view — before metric check so it doesn't fall through
    if (chartType === "gauge") {
        const gaugeValue = Number(rows[0]?.[columns.length - 1] ?? rows[0]?.[0] ?? 0);
        return (
            <div className="flex flex-col items-center justify-center h-[360px]">
                <GaugeChart value={gaugeValue} label={yAxisLabel || columns[columns.length - 1] || "Value"} />
            </div>
        );
    }

    // Metric View
    if (chartType === "metric" || (columns.length === 1 && rows.length === 1)) {
        return (
            <div className="flex flex-col items-center justify-center p-8 bg-gray-950/30 border border-gray-800 rounded-lg min-h-[140px]">
                <span
                    className="text-5xl font-extrabold font-mono"
                    style={{ color: toHex(ACTIVE_COLORS[0]) }}
                >
                    {formatCell(rows[0][0])}
                </span>
                <span className="text-xs text-gray-400 mt-2 uppercase tracking-wider font-semibold">
                    {yAxisLabel || columns[0] || ""}
                </span>
            </div>
        );
    }

    // Prepare Recharts format
    const rechartsData = rows.map(row => {
        const item: any = {};
        item.name = formatFieldName(String(row[0] ?? ""));
        columns.slice(1).forEach((col, idx) => {
            item[col] = Number(row[idx + 1]) || 0;
        });
        return item;
    });

    const valueKeys = columns.slice(1);
    const normalizedType = normalizeChartType(chartType);

    if (normalizedType === "pie") {
        const pieData = rows.map((row, index) => ({
            name: formatFieldName(String(row[0] ?? "")),
            value: Number(row[1]) || 0,
            fill: toHex(ACTIVE_COLORS[index % ACTIVE_COLORS.length])
        }));
        const total = pieData.reduce((sum, item) => sum + item.value, 0);

        return (
            <div className="grid grid-cols-[1fr_200px] gap-4 h-[380px] items-center">
                <div className="h-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <RePieChart>
                            <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius="80%"
                                innerRadius="60%"
                                stroke="none"
                            >
                                {pieData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                        </RePieChart>
                    </ResponsiveContainer>
                </div>
                <div className="max-h-[200px] overflow-y-auto custom-scroll border border-gray-800 rounded-lg p-3 bg-gray-950/20">
                    <div className="space-y-1.5">
                        {pieData.map((item, index) => {
                            const percent = total > 0 ? `${((item.value / total) * 100).toFixed(0)}%` : "0%";
                            return (
                                <div key={index} className="flex items-center gap-2 text-[10px] text-gray-300">
                                    {onColorPaletteChange ? (
                                        <label className="relative cursor-pointer shrink-0 w-2.5 h-2.5 rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                                            <span className="w-full h-full rounded-full" style={{ backgroundColor: item.fill }} />
                                            <input
                                                type="color"
                                                value={item.fill}
                                                onChange={(e) => {
                                                    const next = [...(colorPalette || [])];
                                                    for (let i = 0; i <= index; i++) {
                                                        if (next[i] === undefined) {
                                                            next[i] = ACTIVE_COLORS[i % ACTIVE_COLORS.length];
                                                        }
                                                    }
                                                    next[index] = e.target.value;
                                                    onColorPaletteChange(next);
                                                }}
                                                className="absolute inset-0 w-0 h-0 opacity-0 pointer-events-none"
                                            />
                                        </label>
                                    ) : (
                                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.fill }} />
                                    )}
                                    <span className="truncate flex-1 font-semibold">{item.name}</span>
                                    <span className="text-gray-500 font-mono">{percent} ({item.value})</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="relative w-full min-h-[350px] h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
                {normalizedType === "area" ? (
                    <ReAreaChart
                        data={rechartsData}
                        margin={{ top: 10, right: 10, left: 10, bottom: xAxisLabel ? 28 : 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                        <XAxis
                            dataKey="name"
                            stroke="#9ca3af"
                            fontSize={10}
                            label={xAxisLabel ? { value: xAxisLabel, position: "insideBottom", offset: -10, fill: "#6b7280", fontSize: 10, fontWeight: 600 } : undefined}
                        />
                        <YAxis
                            stroke="#9ca3af"
                            fontSize={10}
                            width={yAxisLabel ? 52 : 40}
                            label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: "insideLeft", offset: 12, fill: "#6b7280", fontSize: 10, fontWeight: 600 } : undefined}
                        />
                        {/* <Tooltip content={<CustomTooltip />} cursor={false} /> */}
                        <Legend verticalAlign="top" align="right" formatter={(value) => formatFieldName(value)} wrapperStyle={{ fontSize: 10, paddingBottom: 10 }} />
                        {valueKeys.map((col, index) => (
                            <Area
                                key={col}
                                type="monotone"
                                dataKey={col}
                                stroke={toHex(ACTIVE_COLORS[index % ACTIVE_COLORS.length])}
                                fill={toHex(ACTIVE_COLORS[index % ACTIVE_COLORS.length])}
                                fillOpacity={0.2}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={makeActiveDot(col, toHex(ACTIVE_COLORS[index % ACTIVE_COLORS.length]))}
                            />
                        ))}
                        {threshold?.show && (
                            <ReferenceLine y={threshold.value} stroke={threshold.color || "#E7664C"} strokeDasharray="3 3" />
                        )}
                    </ReAreaChart>
                ) : normalizedType === "line" ? (
                    <ReLineChart
                        data={rechartsData}
                        margin={{ top: 10, right: 10, left: 10, bottom: xAxisLabel ? 28 : 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                        <XAxis
                            dataKey="name"
                            stroke="#9ca3af"
                            fontSize={10}
                            label={xAxisLabel ? { value: xAxisLabel, position: "insideBottom", offset: -10, fill: "#6b7280", fontSize: 10, fontWeight: 600 } : undefined}
                        />
                        <YAxis
                            stroke="#9ca3af"
                            fontSize={10}
                            width={yAxisLabel ? 52 : 40}
                            label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: "insideLeft", offset: 12, fill: "#6b7280", fontSize: 10, fontWeight: 600 } : undefined}
                        />
                        {/* <Tooltip content={<CustomTooltip />} /> */}
                        <Legend verticalAlign="top" align="right" formatter={(value) => formatFieldName(value)} wrapperStyle={{ fontSize: 10, paddingBottom: 10 }} />
                        {valueKeys.map((col, index) => (
                            <Line
                                key={col}
                                type="monotone"
                                dataKey={col}
                                stroke={toHex(ACTIVE_COLORS[index % ACTIVE_COLORS.length])}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={makeActiveDot(col, toHex(ACTIVE_COLORS[index % ACTIVE_COLORS.length]))}

                            />
                        ))}
                        {threshold?.show && (
                            <ReferenceLine y={threshold.value} stroke={threshold.color || "#E7664C"} strokeDasharray="3 3" />
                        )}
                    </ReLineChart>
                ) : (
                    <BarChart
                        data={rechartsData}
                        layout={chartType === "horizontal_bar" ? "vertical" : "horizontal"}
                        margin={{
                            top: 10,
                            right: 10,
                            left: chartType === "horizontal_bar" ? 80 : (yAxisLabel ? 10 : -10),
                            bottom: xAxisLabel && chartType !== "horizontal_bar" ? 28 : 5,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                        {chartType === "horizontal_bar" ? (
                            <>
                                <XAxis
                                    type="number"
                                    stroke="#9ca3af"
                                    fontSize={10}
                                    label={yAxisLabel ? { value: yAxisLabel, position: "insideBottom", offset: -10, fill: "#6b7280", fontSize: 10, fontWeight: 600 } : undefined}
                                />
                                <YAxis
                                    dataKey="name"
                                    type="category"
                                    stroke="#9ca3af"
                                    fontSize={10}
                                    width={80}
                                    label={xAxisLabel ? { value: xAxisLabel, angle: -90, position: "insideLeft", offset: 12, fill: "#6b7280", fontSize: 10, fontWeight: 600 } : undefined}
                                />
                            </>
                        ) : (
                            <>
                                <XAxis
                                    dataKey="name"
                                    stroke="#9ca3af"
                                    fontSize={10}
                                    tick={{ fill: "#9ca3af" }}
                                    label={xAxisLabel ? { value: xAxisLabel, position: "insideBottom", offset: -12, fill: "#6b7280", fontSize: 10, fontWeight: 600 } : undefined}
                                />
                                <YAxis
                                    stroke="#9ca3af"
                                    fontSize={10}
                                    label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: "insideLeft", offset: 12, fill: "#6b7280", fontSize: 10, fontWeight: 600 } : undefined}
                                    width={yAxisLabel ? 52 : 40}
                                />
                            </>
                        )}

                        <Tooltip content={<CustomTooltip />} cursor={false} />
                        <Legend verticalAlign="top" align="right" formatter={(value) => formatFieldName(value)} wrapperStyle={{ fontSize: 10, paddingBottom: 10 }} />
                        {valueKeys.map((col, index) => (
                            <Bar
                                key={col}
                                dataKey={col}
                                fill={toHex(ACTIVE_COLORS[index % ACTIVE_COLORS.length])}
                                radius={chartType === "horizontal_bar" ? [0, 4, 4, 0] : [4, 4, 0, 0]}
                            />
                        ))}
                        {threshold?.show && (
                            chartType === "horizontal_bar" ? (
                                <ReferenceLine x={threshold.value} stroke={threshold.color || "#E7664C"} strokeDasharray="3 3" />
                            ) : (
                                <ReferenceLine y={threshold.value} stroke={threshold.color || "#E7664C"} strokeDasharray="3 3" />
                            )
                        )}
                    </BarChart>
                )}
            </ResponsiveContainer>
            {dotTooltip && (
                <div
                    className="absolute z-50 pointer-events-none bg-gray-900/95 backdrop-blur-md border border-gray-700/50 rounded-lg p-2.5 shadow-2xl text-[11px] text-white -translate-x-1/2 -translate-y-full"
                    style={{ left: dotTooltip.x, top: dotTooltip.y - 12 }}
                >
                    <div className="flex items-center gap-2">
                        <span
                            className="inline-block w-2 h-2 rounded-full shrink-0"
                            style={{ backgroundColor: dotTooltip.color }}
                        />
                        <span className="text-gray-300 font-medium">{dotTooltip.name}</span>
                        <span className="font-bold text-white font-mono ml-4">
                            {dotTooltip.value?.toLocaleString()}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}

function normalizeChartType(type: string): "pie" | "line" | "area" | "bar" {
    const t = type.toLowerCase();
    if (t === "pie" || t === "donut" || t === "tag_cloud" || t === "region_map") return "pie";
    if (t === "area") return "area";
    if (t === "line" || t === "timeline" || t === "tsvb") return "line";
    // vertical_bar, bar, horizontal_bar, vis_builder, etc. → bar
    return "bar";
}

function formatCell(val: any) {
    if (val === null || val === undefined) return "";
    if (typeof val === "object") return JSON.stringify(val);
    if (typeof val === "number") return val.toLocaleString();
    return String(val);
}

function formatFieldName(field: string): string {
    if (!field) return "";
    return field
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

interface PreviewWidgetModalProps {
    isOpen: boolean;
    onClose: () => void;
    widgetId: string | null;
    clientId: any;
}

function PreviewWidgetModal({ isOpen, onClose, widgetId, clientId }: PreviewWidgetModalProps) {
    const [loading, setLoading] = useState(false);
    const [widget, setWidget] = useState<any>(null);
    const [previewResult, setPreviewResult] = useState<any>(null);

    useEffect(() => {
        if (!isOpen || !widgetId) {
            setWidget(null);
            setPreviewResult(null);
            return;
        }

        const fetchAndExecute = async () => {
            setLoading(true);
            try {
                // Fetch single widget
                const widgetRes = await baseapi.get(`/analytics/widgets/${widgetId}`, {
                    params: { client_id: clientId }
                });

                const widgetData = widgetRes.data?.data || widgetRes.data;
                if (widgetData && typeof widgetData.visualization_config === "string") {
                    try {
                        widgetData.visualization_config = JSON.parse(widgetData.visualization_config);
                    } catch (e) {
                        console.error("Failed to parse visualization_config in PreviewWidgetModal:", e);
                    }
                }
                console.log("widget data", widgetData?.visualization_config?.color_palette)
                setWidget(widgetData);

                if (widgetData?.query_definition) {
                    const queryRes = await baseapi.post("/analytics/query", widgetData.query_definition, {
                        params: { client_id: clientId }
                    });
                    const queryData = queryRes.data?.data !== undefined ? queryRes.data.data : queryRes.data;
                    setPreviewResult(queryData);
                } else {
                    showError("Widget has no query definition");
                }
            } catch (err: any) {
                console.error("Failed to load widget preview:", err);
                showError(err.message || "Failed to load widget preview");
            } finally {
                setLoading(false);
            }
        };

        fetchAndExecute();
    }, [isOpen, widgetId, clientId]);
    // useEffect(() => {
    //     console.log("widget data after effect", widget)
    // }, [widget]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between bg-gray-800/50">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-orange-500/10 rounded">
                            <Eye className="w-4 h-4 text-orange-400" />
                        </div>
                        <div className="flex flex-col">
                            <h3 className="text-base font-bold text-white leading-normal">
                                {loading ? "Loading Preview..." : widget?.name || "Widget Preview"}
                            </h3>
                            {widget?.description && (
                                <p className="text-[10px] text-gray-400 font-medium tracking-wide">
                                    {widget.description}
                                </p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body / Preview Area */}
                <div className="p-6 flex-1 flex flex-col justify-center items-center relative overflow-hidden min-h-[300px]">
                    {loading ? (
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                            <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">Fetching widget query & data...</span>
                        </div>
                    ) : previewResult ? (
                        <div className="w-full h-full flex flex-col">
                            <div className="flex-1 w-full min-h-[350px]">
                                <DataViz
                                    data={previewResult}
                                    chartType={widget?.chart_type || "bar"}
                                    xAxisLabel={widget?.visualization_config?.x_axis_label || ""}
                                    yAxisLabel={widget?.visualization_config?.y_axis_label || "Count"}
                                    colorPalette={widget?.visualization_config?.color_palette ?? []}
                                    threshold={widget?.visualization_config?.threshold}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="text-center p-8 flex flex-col items-center gap-3">
                            <div className="p-4 bg-red-500/10 rounded-full">
                                <X className="w-8 h-8 text-red-500" />
                            </div>
                            <h4 className="text-sm font-bold text-white uppercase tracking-wider">No Data Available</h4>
                            <p className="text-xs text-gray-500 max-w-xs leading-relaxed">
                                We couldn't load the chart data for this widget.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-4 border-t border-gray-800 bg-gray-800/50 flex items-center justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-transparent border border-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
